import { WEBGL, cameraView, cameraProj, mat4Mul, mat4Invert, PLANE, createConstraint, WORLD, AXIS, DIAL, POINT, mapDirection, pixelRatio, rayHitSphere, unproject, SPHERE, dirFromAzEl, DIRECTION, mapLocation, MATRIX, PoseHelm, cameraToPose, EYE, SELF, qToMat4, cameraEye, cameraFromPose, CameraTrack, PoseTrack, qFromAxisAngle, NDC, mat4ToTransform, meshBounds, meshFlatten, meshNormals, meshGroups, SCREEN, cameraCopy, createCamera, coastAlive, coastStep, cameraOrbit, cameraPan, cameraDolly } from '@nakednous/tree';

/**
 * @file The view bag — the matrices every pick, solve, label and orbit reads.
 * @module host/view
 * @license AGPL-3.0-only
 *
 * The matrices bag mapLocation takes, plus the signed viewport and the NDC
 * convention:
 *
 *   view = {
 *     mat4Proj, mat4View, mat4PV, mat4PVInv,   // Float32Array(16) each, host-owned
 *     mat4Eye,                                 // inverse of mat4View — EYE → WORLD
 *     vp,                                      // [0, h, w, −h] — y-down, logical canvas px
 *     ndcZMin,                                 // WEBGL | WEBGPU, set once at creation
 *     stale,                                   // true until a set() with an invertible P · V
 *     set(P, V)                                // copy P and V, recompute PV and PVInv
 *     setCamera(cam)                           // build V and P from a camera state, then set
 *     resize(width, height)                    // the canvas observer's write into vp
 *   }
 *
 * mat4PVInv and mat4Eye are recomputed on set, once per frame, so unproject
 * and every mapping from SCREEN or EYE share them. A degenerate P · V or V
 * leaves the previous inverses and marks the bag stale; consumers treat a
 * stale bag as "no pick this frame".
 */


const _inv = new Float32Array(16);   // inverse scratch: the bag keeps its previous inverse on failure
const _P   = new Float32Array(16);   // setCamera's projection
const _V   = new Float32Array(16);   // setCamera's view

function _identity(m) {
  m.fill(0);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

/**
 * Create a view bag. The matrices start at identity and the bag stale, so a
 * consumer reading it before the first set() picks nothing.
 *
 * @param {{ ndcZMin?:number, width?:number, height?:number }} [opts]
 *        ndcZMin: WEBGL (−1, default) or WEBGPU (0) — written once here, never
 *        detected. width / height: the initial logical canvas size.
 * @returns {object} The view bag.
 */
function createView(opts) {
  const o = opts || {};
  const w = o.width || 0, h = o.height || 0;
  const view = {
    mat4Proj:  _identity(new Float32Array(16)),
    mat4View:  _identity(new Float32Array(16)),
    mat4PV:    _identity(new Float32Array(16)),
    mat4PVInv: _identity(new Float32Array(16)),
    mat4Eye:   _identity(new Float32Array(16)),
    vp: [0, h, w, -h],
    ndcZMin: o.ndcZMin === 0 ? 0 : WEBGL,
    stale: true,

    /**
     * Install a projection and a view: both copied, P · V with its inverse
     * and the eye matrix (V's inverse) recomputed. A singular product or
     * view keeps the previous inverses and marks the bag stale.
     * @param {ArrayLike<number>} P  Projection mat4.
     * @param {ArrayLike<number>} V  View mat4 (world → eye).
     * @returns {object} this
     */
    set(P, V) {
      for (let i = 0; i < 16; i++) { view.mat4Proj[i] = P[i]; view.mat4View[i] = V[i]; }
      mat4Mul(view.mat4PV, view.mat4Proj, view.mat4View);
      if (mat4Invert(_inv, view.mat4PV) === null) { view.stale = true; return view; }
      view.mat4PVInv.set(_inv);
      if (mat4Invert(_inv, view.mat4View) === null) { view.stale = true; return view; }
      view.mat4Eye.set(_inv);
      view.stale = false;
      return view;
    },

    /**
     * Install a camera state: cameraView and cameraProj at the viewport's
     * aspect under the bag's ndcZMin, then set(). A state with both fov and
     * halfHeight null keeps the projection already installed.
     * @param {object} cam  Camera state.
     * @returns {object} this
     */
    setCamera(cam) {
      const aspect = view.vp[3] !== 0 ? view.vp[2] / -view.vp[3] : 1;
      cameraView(_V, cam);
      const P = cameraProj(_P, cam, aspect, view.ndcZMin) || view.mat4Proj;
      return view.set(P, _V);
    },

    /**
     * Write a new logical canvas size into vp: [0, height, width, −height].
     * @param {number} width
     * @param {number} height
     * @returns {object} this
     */
    resize(width, height) {
      view.vp[1] = height; view.vp[2] = width; view.vp[3] = -height;
      return view;
    },
  };
  return view;
}

/**
 * @file The canvas observer — logical size and device pixel ratio.
 * @module host/canvas
 * @license AGPL-3.0-only
 *
 * A ResizeObserver on the canvas element plus devicePixelRatio tracking:
 * on change the observer reports the logical size in CSS px and the dpr to
 * its callback, which the context uses to update host.width / host.height /
 * host.dpr, the view bag's vp, the label layer and the bridge's onSize. The
 * observer never touches canvas.width / canvas.height — that is the
 * renderer's decision.
 *
 * Without a ResizeObserver (a headless run) the size is measured once from
 * the element's bounding rectangle, falling back to its width / height
 * attributes.
 */


/**
 * Measure a canvas: logical CSS size from its bounding rectangle when laid
 * out, else its width / height attributes; dpr from the window.
 * @param {HTMLCanvasElement} canvas
 * @param {number[]} out3  [width, height, dpr] destination.
 * @returns {number[]} out3
 */
function measureCanvas(canvas, out3) {
  const r = typeof canvas.getBoundingClientRect === 'function' ? canvas.getBoundingClientRect() : null;
  const laidOut = r && r.width > 0 && r.height > 0;
  out3[0] = laidOut ? r.width  : (canvas.width  || 0);
  out3[1] = laidOut ? r.height : (canvas.height || 0);
  out3[2] = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  return out3;
}

/**
 * Observe a canvas's logical size and dpr. `onChange(width, height, dpr)`
 * fires once immediately with the current measure and again on every
 * change; a change is reported only when a value differs.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {function(number, number, number):void} onChange
 * @returns {{ width:number, height:number, dpr:number, measure():void, dispose():void }}
 */
function observeCanvas(canvas, onChange) {
  const m = [0, 0, 1];
  const obs = { width: -1, height: 0, dpr: 1, measure, dispose };
  let ro = null;
  const onWindow = () => measure();

  function measure() {
    measureCanvas(canvas, m);
    if (m[0] === obs.width && m[1] === obs.height && m[2] === obs.dpr) return;
    obs.width = m[0]; obs.height = m[1]; obs.dpr = m[2];
    onChange(obs.width, obs.height, obs.dpr);
  }

  function dispose() {
    if (ro) { ro.disconnect(); ro = null; }
    if (typeof window !== 'undefined' && window.removeEventListener) window.removeEventListener('resize', onWindow);
  }

  measure();                                   // width −1 makes the first measure report
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => measure());
    ro.observe(canvas);
  }
  // A zoom changes devicePixelRatio and fires resize; the observer alone would miss it.
  if (typeof window !== 'undefined' && window.addEventListener) window.addEventListener('resize', onWindow);
  return obs;
}

/**
 * @file The pointer source — a typed event buffer with claims, one per canvas.
 * @module host/pointer
 * @license AGPL-3.0-only
 *
 * Shared by every handle, router and orbit on a canvas. Listeners only
 * record; nothing solves in a listener:
 *
 * - pointerdown · pointermove · pointerup · pointercancel on the canvas,
 *   keydown (Esc) on the window.
 * - Coordinates in screen space — the surface's logical pixels, top-left, y
 *   down — through the element's bounding rectangle: (clientX − rect.left) ·
 *   (width / rect.width), the logical size read off the view bag's vp. These
 *   are the numbers mapLocation(SCREEN) and vp expect (a viewport of negative
 *   height), so a CSS-scaled canvas maps correctly.
 * - Per pointer: an entry { id, x, y, seq, down, up, cancel, owner }, kept
 *   from its press until the frame after its release; `seq` counts moves,
 *   so a consumer remembers the last seq it consumed and reads only new
 *   motion. Multitouch is per-pointer by construction; the mouse is one
 *   more pointer.
 * - Presses queue in order for the frame (`presses`); consumers read the
 *   queue inside the frame and claim what they take. Several same-frame
 *   presses on different members all land.
 * - Claims: claim(id, owner) captures the pointer on the canvas so a drag
 *   that leaves it keeps flowing; release(id), up and cancel release the
 *   capture. An unclaimed pointer is what the orbit sees.
 * - Esc sets the cancel flag on every claimed pointer.
 * - flush() ends the frame: the press queue empties and released pointers
 *   leave the map. The loop calls it after onFrame; an external loop calls
 *   it after its consumers ran (the p5 adapter from postdraw).
 */


/**
 * Create the pointer source of a canvas.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {object} view  The view bag; vp[2] and −vp[3] are the logical size.
 * @returns {object} The source: { presses, pointers, get, claim, release,
 *          ownerOf, flush, dispose }.
 */
function createPointer(canvas, view) {
  const pointers = new Map();          // id → entry
  const presses  = [];                 // this frame's presses, in order: { id, x, y }
  const xy = [0, 0];

  const toCanvas = (e, out) => {
    const r = canvas.getBoundingClientRect ? canvas.getBoundingClientRect() : null;
    const w = view.vp[2], h = -view.vp[3];
    if (r && r.width > 0 && r.height > 0 && w > 0 && h > 0) {
      out[0] = (e.clientX - r.left) * (w / r.width);
      out[1] = (e.clientY - r.top)  * (h / r.height);
    } else {
      out[0] = e.clientX; out[1] = e.clientY;
    }
    return out;
  };

  const entry = (id) => {
    let p = pointers.get(id);
    if (!p) { p = { id, x: 0, y: 0, seq: 0, down: false, up: false, cancel: false, owner: null }; pointers.set(id, p); }
    return p;
  };

  const capture = (id, on) => {
    const f = on ? canvas.setPointerCapture : canvas.releasePointerCapture;
    if (typeof f !== 'function') return;
    try { f.call(canvas, id); } catch (_) { /* best effort: a synthetic or already-released id */ }
  };

  const onDown = (e) => {
    const p = entry(e.pointerId);
    toCanvas(e, xy);
    p.x = xy[0]; p.y = xy[1]; p.seq++;
    p.down = true; p.up = false; p.cancel = false;
    presses.push({ id: p.id, x: p.x, y: p.y });
  };
  const onMove = (e) => {
    const p = entry(e.pointerId);
    toCanvas(e, xy);
    p.x = xy[0]; p.y = xy[1]; p.seq++;
  };
  const onUp = (e) => {
    const p = pointers.get(e.pointerId);
    if (!p) return;
    toCanvas(e, xy);
    p.x = xy[0]; p.y = xy[1]; p.seq++;
    p.up = true;
    capture(p.id, false);
  };
  const onCancel = (e) => {
    const p = pointers.get(e.pointerId);
    if (!p) return;
    p.cancel = true;
    capture(p.id, false);
  };
  const onKey = (e) => {
    if (e.key !== 'Escape') return;
    for (const p of pointers.values()) if (p.owner !== null) p.cancel = true;
  };

  canvas.addEventListener('pointerdown',   onDown);
  canvas.addEventListener('pointermove',   onMove);
  canvas.addEventListener('pointerup',     onUp);
  canvas.addEventListener('pointercancel', onCancel);
  if (typeof window !== 'undefined' && window.addEventListener) window.addEventListener('keydown', onKey);

  const source = {
    /** This frame's presses in order, each { id, x, y }; emptied by flush(). */
    presses,
    /** The live pointer entries by id. */
    pointers,

    /**
     * The entry of a pointer, or null when it is not on the canvas.
     * @param {number} id
     * @returns {object|null}
     */
    get(id) { return pointers.get(id) || null; },

    /**
     * Claim a pointer for an owner and capture it on the canvas. Fails when
     * another owner holds it or the pointer is unknown.
     * @param {number} id
     * @param {object} owner
     * @returns {boolean} claimed
     */
    claim(id, owner) {
      const p = pointers.get(id);
      if (!p || (p.owner !== null && p.owner !== owner)) return false;
      p.owner = owner;
      capture(id, true);
      return true;
    },

    /**
     * Release a claim and the capture with it. No-op for an unclaimed id.
     * @param {number} id
     */
    release(id) {
      const p = pointers.get(id);
      if (!p || p.owner === null) return;
      p.owner = null;
      capture(id, false);
    },

    /**
     * The owner holding a pointer, or null.
     * @param {number} id
     * @returns {object|null}
     */
    ownerOf(id) { const p = pointers.get(id); return p ? p.owner : null; },

    /**
     * End the frame: the press queue empties; pointers that released or
     * cancelled leave the map (their claims dropped), the rest keep their
     * position and seq.
     */
    flush() {
      presses.length = 0;
      for (const [id, p] of pointers) {
        if (p.up || p.cancel) { p.owner = null; pointers.delete(id); }
      }
    },

    /** Remove every listener and release every capture. */
    dispose() {
      canvas.removeEventListener('pointerdown',   onDown);
      canvas.removeEventListener('pointermove',   onMove);
      canvas.removeEventListener('pointerup',     onUp);
      canvas.removeEventListener('pointercancel', onCancel);
      if (typeof window !== 'undefined' && window.removeEventListener) window.removeEventListener('keydown', onKey);
      for (const p of pointers.values()) if (p.owner !== null) capture(p.id, false);
      pointers.clear();
      presses.length = 0;
    },
  };
  return source;
}

/**
 * @file The players registry and the frame loop.
 * @module host/loop
 * @license AGPL-3.0-only
 *
 * A player is { tick(dt) → boolean }, removed from the registry when it
 * returns false. Iteration snapshots the set, so a player may remove itself
 * or add another during a tick. Tracks register through their activation
 * hooks, helms until disposed.
 *
 * The loop is optional: with it, requestAnimationFrame (overridable — the
 * seam a WebXR session fills) drives onFrame(dt) with dt in seconds clamped
 * to 50 ms, the stalled-tab guard; without it the application ticks the
 * players from its own loop.
 */


/**
 * Create a players registry.
 * @returns {{ add(player):object, remove(player):object, has(player):boolean,
 *             tick(dt:number):void, size:number, clear():void }}
 */
function createPlayers() {
  const set = new Set();
  const players = {
    /** Register a player; adding one already present is a no-op. */
    add(player) { if (player) set.add(player); return players; },
    /** Unregister a player. */
    remove(player) { set.delete(player); return players; },
    /** Whether a player is registered. */
    has(player) { return set.has(player); },
    /** Tick every player with dt (seconds); one returning false is removed. */
    tick(dt) {
      for (const p of [...set]) {
        if (!set.has(p)) continue;                 // removed by an earlier player this tick
        if (p.tick(dt) === false) set.delete(p);
      }
    },
    /** Registered count. */
    get size() { return set.size; },
    /** Unregister every player. */
    clear() { set.clear(); },
  };
  return players;
}

const _now$2 = () => (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

/**
 * Create a frame loop. `onFrame(dt)` runs once per animation frame with the
 * clamped period in seconds; start() schedules, stop() cancels.
 *
 * @param {{ onFrame:function(number):void, raf?:function, caf?:function,
 *           clampMs?:number, now?:function():number }} opts
 *        raf / caf default to the window's requestAnimationFrame /
 *        cancelAnimationFrame; clampMs defaults to 50.
 * @returns {{ start():object, stop():object, running:boolean, dt:number }}
 */
function createLoop(opts) {
  const o = opts || {};
  const raf = o.raf || ((cb) => requestAnimationFrame(cb));
  const caf = o.caf || ((id) => cancelAnimationFrame(id));
  const now = o.now || _now$2;
  const clamp = (o.clampMs ?? 50) / 1000;
  let handle = null, last = 0;
  const loop = {
    running: false,
    dt: 0,
    start() {
      if (loop.running) return loop;
      loop.running = true;
      last = now();
      const frame = () => {
        if (!loop.running) return;
        const t = now();
        loop.dt = Math.min((t - last) / 1000, clamp);
        last = t;
        o.onFrame(loop.dt);
        if (loop.running) handle = raf(frame);
      };
      handle = raf(frame);
      return loop;
    },
    stop() {
      loop.running = false;
      if (handle !== null) { caf(handle); handle = null; }
      return loop;
    },
  };
  return loop;
}

/**
 * @file The handle controller — a draggable constraint driven from the pointer source.
 * @module host/handle
 * @license AGPL-3.0-only
 *
 * Wraps a core Constraint (@nakednous/tree) with the transport a draggable
 * 3D control needs: presses, moves and releases from the host's pointer
 * source, a pixel → ray unprojection through the host's view bag, an
 * analytic pick against the constraint's proxy, and a host-driven update()
 * lifecycle. No drawing: a bridge draws the locus (locusLines) and the dot.
 *
 * ── update() ordering contract ─────────────────────────────────────────────
 * update() is host-driven, before the orbit, because the orbit gate depends
 * on the grab resolving first:
 *
 *   onFrame() { setCamera(cam); if (!h.update()) orbit.update(); scene() }
 *
 * ── Pick ───────────────────────────────────────────────────────────────────
 * The pointer's ray is unproject(x, y, view) in WORLD; the grab size is
 * grabPx converted to world units through pixelRatio at the proxy's depth;
 * the constraint's proxy(ray, radius) answers t or Infinity. Built-in kinds
 * carry their proxy (a sphere at the point, the DIAL's ring); a custom kind
 * supplies one or gets the sphere. solve() runs in WORLD; value() converts.
 *
 * ── Pointer source ─────────────────────────────────────────────────────────
 * update() reads the frame's press queue and the tracked pointer's entry
 * (seq counts moves; up / cancel end the gesture; Esc cancels every claimed
 * pointer). A grab claims the pointer, which captures it on the canvas; a
 * miss leaves it unclaimed for the orbit. The whole gesture keys to one
 * pointerId, so on a shared surface each handle tracks its own finger.
 *
 * ── Constraint kinds ───────────────────────────────────────────────────────
 * Core SPHERE / PLANE / AXIS / DIAL pass straight through. VIEW is a core
 * PLANE re-aimed at the camera each solve — a screen-parallel drag plane
 * through the current point — reported as a world position; its constraint
 * carries `view: true`, the flag locusLines reads. A custom kind is a
 * contract-conforming object (kind / solve / value / seed, optional scalar /
 * azEl / aim / proxy / locus) passed as `constraint`.
 *
 * ── Snap / hover / cancel / from ───────────────────────────────────────────
 * snap quantises at the solve seam: an angular step for SPHERE (az/el) and
 * DIAL (θ), a world grid for PLANE / AXIS / VIEW (PLANE re-projecting).
 * hover is a lone-handle opt-in (a router provides it shared). Cancel — Esc,
 * pointercancel, cancel() — reverts to the value captured at grab, restores
 * the binding and fires onCancel instead of onRelease. `from` names the space
 * the symbolic basis (axis / normal / zero) resolves FROM into WORLD each idle
 * frame through mapDirection on the view bag — WORLD, EYE, or a mat4 —
 * frozen at grab.
 */


/** The screen-parallel free-translate constraint (a core PLANE re-aimed each solve). */
const VIEW = 4;

// Module-level scratch — update() and value() run to completion within one
// frame with no reentrancy across handles.
const _rayO = [0, 0, 0];             // pick ray origin, WORLD
const _rayD = [0, 0, 0];             // pick ray unit direction, WORLD
/** The ray scratch a handle's _ray(x, y) fills — the router's shared pick reads it. */
const rayScratch = { o: _rayO, d: _rayD };
const _v3   = [0, 0, 0];             // value() extraction scratch
const _q2   = [0, 0];                // az/el snap scratch
const _b0   = [0, 0, 0];             // resolved θ = 0 reference
const _b2   = [0, 0, 0];             // resolved axis / normal
const _frameInv = new Float32Array(16);

// The mapping bag mapLocation / mapDirection take: the view's matrices plus
// the frames a call names (a mat4 `from` or `to` is the MATRIX space).
const _bag = { mat4Proj: null, mat4View: null, mat4PV: null, mat4PVInv: null, mat4Eye: null,
               fromFrame: null, toFrame: null, toFrameInv: null };
const _isMat = (s) => s != null && typeof s !== 'string';
const _space = (s) => (_isMat(s) ? MATRIX : s);

function _bagOf(view, from, to) {
  _bag.mat4Proj = view.mat4Proj; _bag.mat4View = view.mat4View;
  _bag.mat4PV = view.mat4PV; _bag.mat4PVInv = view.mat4PVInv; _bag.mat4Eye = view.mat4Eye;
  _bag.fromFrame = _isMat(from) ? from : null;
  if (_isMat(to)) { _bag.toFrame = to; _bag.toFrameInv = mat4Invert(_frameInv, to) ? _frameInv : null; }
  else { _bag.toFrame = null; _bag.toFrameInv = null; }
  return _bag;
}

const _norm3 = (o) => {
  const l = Math.hypot(o[0], o[1], o[2]) || 1;
  o[0] /= l; o[1] /= l; o[2] /= l;
  return o;
};

// Orthonormal in-plane basis for a unit normal n, seeded from the world axis
// least aligned with it (a DIAL's θ = 0 reference when `from` gives none).
const _basisFromNormal = (n, ub, vb) => {
  const ax = Math.abs(n[0]), ay = Math.abs(n[1]), az = Math.abs(n[2]);
  let rx = 0, ry = 0, rz = 0;
  if (ax <= ay && ax <= az) rx = 1; else if (ay <= az) ry = 1; else rz = 1;
  ub[0] = ry*n[2] - rz*n[1]; ub[1] = rz*n[0] - rx*n[2]; ub[2] = rx*n[1] - ry*n[0];
  _norm3(ub);
  vb[0] = n[1]*ub[2] - n[2]*ub[1]; vb[1] = n[2]*ub[0] - n[0]*ub[2]; vb[2] = n[0]*ub[1] - n[1]*ub[0];
};

// Read component i of an array or {x, y, z}, falling back to d.
const _vx$1 = (v, i, d) => {
  if (v == null) return d;
  const c = i === 0 ? v.x : i === 1 ? v.y : v.z;
  return c ?? v[i] ?? d;
};

/** Contract check for a custom constraint object. */
const isConstraint = (c) =>
  c && typeof c === 'object' &&
  typeof c.solve === 'function' &&
  typeof c.value === 'function' &&
  typeof c.seed  === 'function';

/**
 * The handle controller. Stateful and long-lived, like a track; driven from
 * the frame through update(), read with value(out, opts).
 */
class Handle {
  /**
   * @param {object} host  The host context (its pointer source and view bag).
   * @param {object} opts  See host.handle().
   */
  constructor(host, opts) {
    this._host = host;
    this._pointer = host.pointer;
    this._view = host.view;

    const kind = opts.constraint;
    this._isView = (kind === VIEW);
    if (isConstraint(kind)) {
      this._constraint = kind;
    } else {
      const coreKind = this._isView ? PLANE : kind;
      // Vector opts pass straight through — the core duck-types arrays and {x, y, z}.
      this._constraint = createConstraint(coreKind, {
        radius: opts.radius, report: opts.report, anchor: opts.anchor,
        normal: opts.normal, axis: opts.axis, zero: opts.zero, extent: opts.extent,
      });
      if (this._isView) this._constraint.view = true;   // the flag locusLines reads
    }

    // Deferred constraint frame: the symbolic basis and the space it resolves from.
    this._from = null; this._fromDir = null; this._fromZero = null;
    if (opts.from != null && opts.from !== WORLD) {
      const custom = isConstraint(kind);
      const dirOpt = (kind === PLANE) ? opts.normal : (opts.axis ?? opts.normal);
      const ok = !this._isView &&
                 (kind === PLANE || kind === AXIS || kind === DIAL ||
                  (custom && typeof this._constraint.aim === 'function'));
      if (!ok) {
        console.error('[host] handle: `from` needs an aimable constraint — PLANE, AXIS, DIAL, or a custom kind exposing aim(); ignoring.');
      } else if (custom && dirOpt == null) {
        console.error('[host] handle: `from` on a custom kind needs a symbolic `axis` (or `normal`) to resolve; ignoring.');
      } else {
        this._from = opts.from;
        this._fromDir = [_vx$1(dirOpt, 0, kind === AXIS ? 1 : 0), _vx$1(dirOpt, 1, kind === AXIS ? 0 : 1), _vx$1(dirOpt, 2, 0)];
        if (opts.zero != null) {
          this._fromZero = [_vx$1(opts.zero, 0, 1), _vx$1(opts.zero, 1, 0), _vx$1(opts.zero, 2, 0)];
        } else if (kind === DIAL) {
          // Derive the θ = 0 reference once, in the FROM space, so axis and zero co-rotate.
          _b2[0] = this._fromDir[0]; _b2[1] = this._fromDir[1]; _b2[2] = this._fromDir[2];
          _norm3(_b2);
          const r0 = [0, 0, 0], r1 = [0, 0, 0];
          _basisFromNormal(_b2, r0, r1);
          this._fromZero = r0;
        }
      }
    }

    this._enabled = opts.enabled !== false;
    this._grabPx  = Number.isFinite(opts.grabPx) ? opts.grabPx : 12;
    this._snap    = opts.snap ?? null;
    this._hover   = opts.hover === true;
    this._hovered = false;
    this._hid = null; this._hseq = -1;       // the pointer last hover-tested, and its seq then

    // Transport state: one pointerId per gesture, its last consumed move seq
    // and its position in logical canvas px; _routed hands the down step to a router.
    this._grabbed = false;
    this._pid = null;
    this._seq = -1;
    this._ptr = [0, 0];
    this._routed = false;

    // The proxy: its world position and the grab radius in world units.
    this._proxyPos = [0, 0, 0];
    this._proxyRad = 0;

    // Cancel state — the value (and scalar, for winding) captured at grab.
    this._saved = [0, 0, 0];
    this._savedS = 0;

    // Hooks — user-facing, then the lib-space seams a router or a track owns.
    this.onGrab    = typeof opts.onGrab    === 'function' ? opts.onGrab    : null;
    this.onRelease = typeof opts.onRelease === 'function' ? opts.onRelease : null;
    this.onChange  = typeof opts.onChange  === 'function' ? opts.onChange  : null;
    this.onCancel  = typeof opts.onCancel  === 'function' ? opts.onCancel  : null;
    this._onGrab = null; this._onRelease = null; this._onChange = null; this._onCancel = null;

    // Binding — a normalised { get, set } accessor; _bindVal the reused value array.
    this._binder = null;
    this._bindVal = [0, 0, 0];

    if (opts.bind != null) this.bind(opts.bind);
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Resolve the grab and re-solve from the pointer. Call first in the frame,
   * before the orbit; a routed handle is updated by its router. Returns the
   * grabbed state so the orbit gate can short-circuit; a disabled handle
   * returns false.
   * @returns {boolean} grabbed
   */
  update() {
    if (!this._enabled) {
      if (this._pid !== null && this._pointer) this._pointer.release(this._pid);
      this._grabbed = false; this._hovered = false; this._pid = null;
      return false;
    }
    const src = this._pointer;
    if (!src) return false;
    if (this._from && !this._grabbed) this._resolveFrame();

    // Fresh press (unrouted): the first unclaimed press this frame is
    // hit-tested at its pixel; a hit claims the pointer, a miss leaves it.
    if (!this._routed && this._pid === null) {
      for (const pr of src.presses) {
        if (src.ownerOf(pr.id) !== null) continue;
        this._ptr[0] = pr.x; this._ptr[1] = pr.y;
        if (this._pickAt(pr.x, pr.y)) { this._adopt(pr.id, pr.x, pr.y); break; }
      }
    }

    if (this._pid !== null) {
      const e = src.get(this._pid);
      if (!e) {
        this._cancelNow();                       // the entry left before this handle saw its end
      } else {
        if (this._grabbed && e.seq !== this._seq && !e.cancel) {
          this._seq = e.seq;
          this._ptr[0] = e.x; this._ptr[1] = e.y;
          this._solveFromPointer(e.x, e.y);
          this._applySnap();
          this._afterSolve();
        }
        if (e.cancel) {
          this._cancelNow();
        } else if (e.up) {
          src.release(this._pid);
          this._grabbed = false;
          this._pid = null;
          this.onRelease  && this.onRelease(this);
          this._onRelease && this._onRelease(this);
        }
      }
    }

    // Hover: a lone handle tests the latest unclaimed pointer motion.
    if (this._grabbed) {
      this._hovered = true;
    } else if (this._hover && !this._routed) {
      for (const e of src.pointers.values()) {
        if (e.owner !== null) continue;
        if (e.id === this._hid && e.seq === this._hseq) continue;
        this._hid = e.id; this._hseq = e.seq;
        this._hovered = this._pickAt(e.x, e.y);
        break;
      }
    }
    return this._grabbed;
  }

  _beginGrab() {
    const c = this._constraint;
    c.value(this._saved, POINT);
    this._savedS = typeof c.scalar === 'function' ? c.scalar() : 0;
    this._grabbed = true;
    this.onGrab  && this.onGrab(this);
    this._onGrab && this._onGrab(this);
  }

  /** Router seam: adopt a pointer a shared pick decided; claims it through the source. */
  _adopt(pointerId, x, y) {
    const e = this._pointer.get(pointerId);
    this._pid = pointerId;
    this._seq = e ? e.seq : -1;
    this._ptr[0] = x; this._ptr[1] = y;
    this._pointer.claim(pointerId, this);
    this._beginGrab();
  }

  /**
   * Revert the drag in flight to the value captured at grab (exact θ winding
   * included), restore the binding, fire onCancel. No-op when not grabbed.
   * Chainable.
   * @returns {Handle} this
   */
  cancel() {
    if (this._grabbed) this._cancelNow();
    return this;
  }

  _cancelNow() {
    const c = this._constraint;
    if (this._isView) {
      c.pt[0] = this._saved[0]; c.pt[1] = this._saved[1]; c.pt[2] = this._saved[2];
    } else if (c.kind === DIAL) {
      c.s = this._savedS;
      c._dialPoint();
    } else {
      c.seed(this._saved[0], this._saved[1], this._saved[2]);
    }
    if (this._binder) this._binder.set(this.value(this._bindVal));
    this._grabbed = false;
    if (this._pid !== null && this._pointer) this._pointer.release(this._pid);
    this._pid = null;
    this.onCancel  && this.onCancel(this);
    this._onCancel && this._onCancel(this);
  }

  // ── Frame, pick, solve ──────────────────────────────────────────────────

  // Resolve the symbolic basis from its space into WORLD through the view
  // bag and re-aim the constraint; idle frames only.
  _resolveFrame() {
    const v = this._view, bag = _bagOf(v, this._from, null), from = _space(this._from);
    const d = this._fromDir;
    mapDirection(_b2, d[0], d[1], d[2], from, WORLD, bag, v.vp, v.ndcZMin);
    if (this._fromZero) {
      const z = this._fromZero;
      mapDirection(_b0, z[0], z[1], z[2], from, WORLD, bag, v.vp, v.ndcZMin);
      this._constraint.aim(_b2[0], _b2[1], _b2[2], _b0[0], _b0[1], _b0[2]);
    } else {
      this._constraint.aim(_b2[0], _b2[1], _b2[2]);
    }
  }

  // The proxy's world position and its grab radius in world units — a
  // constant grabPx canvas size at the proxy's depth.
  _proxyPrep() {
    const c = this._constraint, v = this._view, p = this._proxyPos;
    if (this._from && !this._grabbed) this._resolveFrame();
    if (c.kind === DIAL && !this._isView) {
      p[0] = c.anchor[0]; p[1] = c.anchor[1]; p[2] = c.anchor[2];
    } else {
      c.value(p, POINT);
    }
    const V = v.mat4View;
    const eyeZ = V[2]*p[0] + V[6]*p[1] + V[10]*p[2] + V[14];
    this._proxyRad = this._grabPx * pixelRatio(v.mat4Proj, -v.vp[3], eyeZ, v.ndcZMin);
  }

  // The analytic pick: t along the ray, or Infinity.
  _proxyT(o, d) {
    this._proxyPrep();
    const c = this._constraint;
    if (typeof c.proxy === 'function') return c.proxy(o[0], o[1], o[2], d[0], d[1], d[2], this._proxyRad);
    const p = this._proxyPos;
    return rayHitSphere(o[0], o[1], o[2], d[0], d[1], d[2], p[0], p[1], p[2], this._proxyRad);
  }

  /** Unproject a screen-space pixel into the ray scratch; false on a stale view. */
  _ray(x, y) {
    const v = this._view;
    if (v.stale) return false;
    return unproject(_rayO, _rayD, x, y, v, v.vp, v.ndcZMin) !== null;
  }

  /** Hit-test the proxy at a screen-space pixel. */
  _pickAt(x, y) {
    return this._ray(x, y) && this._proxyT(_rayO, _rayD) < Infinity;
  }

  _solveFromPointer(x, y) {
    if (!this._ray(x, y)) return;
    if (this._isView) this._viewUpdatePlane();
    this._constraint.solve(_rayO[0], _rayO[1], _rayO[2], _rayD[0], _rayD[1], _rayD[2]);
  }

  // VIEW: re-aim the plane at the camera through the current point — its
  // normal the look direction (−Z of the eye matrix), its anchor the point.
  _viewUpdatePlane() {
    const c = this._constraint, E = this._view.mat4Eye;
    c.n[0] = -E[8]; c.n[1] = -E[9]; c.n[2] = -E[10];
    _norm3(c.n);
    c.anchor[0] = c.pt[0]; c.anchor[1] = c.pt[1]; c.anchor[2] = c.pt[2];
  }

  // ── Snap ────────────────────────────────────────────────────────────────

  /** Snap step — angular (rad) for SPHERE / DIAL, a world grid for PLANE / AXIS / VIEW; null off. */
  get snap()  { return this._snap; }
  set snap(v) { this._snap = v ?? null; }

  _applySnap() {
    const sn = this._snap;
    if (sn == null) return;
    const c = this._constraint;
    if (this._isView || c.kind === PLANE) {
      const gx = Array.isArray(sn) ? sn[0] : sn, gy = Array.isArray(sn) ? sn[1] : sn, gz = Array.isArray(sn) ? sn[2] : sn;
      _v3[0] = gx > 0 ? Math.round(c.pt[0] / gx) * gx : c.pt[0];
      _v3[1] = gy > 0 ? Math.round(c.pt[1] / gy) * gy : c.pt[1];
      _v3[2] = gz > 0 ? Math.round(c.pt[2] / gz) * gz : c.pt[2];
      if (this._isView) { c.pt[0] = _v3[0]; c.pt[1] = _v3[1]; c.pt[2] = _v3[2]; }
      else c.seed(_v3[0], _v3[1], _v3[2]);
    } else if (c.kind === AXIS || c.kind === DIAL) {
      const step = Array.isArray(sn) ? sn[0] : sn;
      if (!(step > 0)) return;
      const q = Math.round(c.s / step) * step;
      c.s = q < c.min ? c.min : (q > c.max ? c.max : q);
      if (c.kind === DIAL) c._dialPoint();
      else {
        c.pt[0] = c.anchor[0] + c.s * c.u[0];
        c.pt[1] = c.anchor[1] + c.s * c.u[1];
        c.pt[2] = c.anchor[2] + c.s * c.u[2];
      }
    } else if (c.kind === SPHERE) {
      const step = Array.isArray(sn) ? sn[0] : sn;
      if (!(step > 0)) return;
      c.azEl(_q2);
      dirFromAzEl(c.dir, Math.round(_q2[0] / step) * step, Math.round(_q2[1] / step) * step);
    }
  }

  // ── Value and binding ───────────────────────────────────────────────────

  /**
   * Write the current value into `out`. `report` overrides the constraint's
   * (POINT | DIRECTION); `to` converts from WORLD to EYE, SCREEN, NDC, or a
   * mat4 frame through the view bag. Out-first, out required.
   * @param {number[]} out  3-element destination.
   * @param {{ to?:string|ArrayLike<number>, report?:number }} [opts]
   * @returns {number[]} out
   */
  value(out, opts) {
    const o = opts || {}, c = this._constraint;
    const report = (o.report === POINT || o.report === DIRECTION) ? o.report : c.report;
    const to = o.to ?? WORLD;
    c.value(_v3, report);
    if (to === WORLD) { out[0] = _v3[0]; out[1] = _v3[1]; out[2] = _v3[2]; return out; }
    const v = this._view, bag = _bagOf(v, null, to), space = _space(to);
    return report === DIRECTION
      ? mapDirection(out, _v3[0], _v3[1], _v3[2], WORLD, space, bag, v.vp, v.ndcZMin)
      : mapLocation(out, _v3[0], _v3[1], _v3[2], WORLD, space, bag, v.vp, v.ndcZMin);
  }

  /**
   * Bind the handle to a target it drives while dragging: a `{ get, set }`
   * accessor, or a vec3 array mutated in place (a camera state's eye or
   * center is one). get() seeds the constraint now; each solve calls
   * set(value) with the reused value array, then fires onChange. Chainable.
   * @param {number[]|{ get:Function, set:Function }} target
   * @returns {Handle} this
   */
  bind(target) {
    let binder = null;
    if (target && typeof target.get === 'function' && typeof target.set === 'function') {
      binder = target;
    } else if (target && typeof target === 'object' && typeof target.length === 'number' && target.length >= 3) {
      binder = { get: () => target, set: (v) => { target[0] = v[0]; target[1] = v[1]; target[2] = v[2]; } };
    } else {
      console.error('[host] handle.bind: unrecognised target — pass a vec3 array or an { get, set } accessor. Leaving unbound.');
      return this;
    }
    this._binder = binder;
    if (this._from && !this._grabbed) this._resolveFrame();
    this._seedFromBinding();
    return this;
  }

  /** Re-seed the constraint from the bound target after it changed externally. Chainable. */
  sync() {
    if (this._binder) this._seedFromBinding();
    return this;
  }

  _seedFromBinding() {
    const g = this._binder.get();
    if (g == null) return;
    const x = _vx$1(g, 0, 0), y = _vx$1(g, 1, 0), z = _vx$1(g, 2, 0);
    if (this._isView) { const pt = this._constraint.pt; pt[0] = x; pt[1] = y; pt[2] = z; }
    else this._constraint.seed(x, y, z);
  }

  // Push the freshly solved value to the binding and fire onChange, once per solve.
  _afterSolve() {
    const bound = this._binder !== null;
    const notify = !!(this.onChange || this._onChange);
    if (!bound && !notify) return;
    const v = this.value(this._bindVal);
    if (bound) this._binder.set(v);
    if (notify) {
      this.onChange  && this.onChange(v, this);
      this._onChange && this._onChange(v, this);
    }
  }

  // ── Readouts and edits ──────────────────────────────────────────────────

  /** AXIS: the signed distance along the rail; DIAL: the accumulated angle; NaN otherwise. */
  scalar() { return typeof this._constraint.scalar === 'function' ? this._constraint.scalar() : NaN; }

  /** SPHERE: [az, el] of the current direction, into out2. */
  azEl(out2) {
    const o = out2 || [0, 0];
    return typeof this._constraint.azEl === 'function' ? this._constraint.azEl(o) : o;
  }

  /** True between grab and release. */
  grabbed() { return this._grabbed; }

  /** True while the pointer rests on the proxy, and while grabbed. */
  hovered() { return this._hovered; }

  /**
   * Move the reference point — sphere centre, plane point, axis anchor,
   * dial centre, or a VIEW handle's point; the stored point rides along.
   * Chainable.
   * @param {number[]|{x:number,y:number,z:number}} v
   * @returns {Handle} this
   */
  anchor(v) {
    const c = this._constraint;
    const t = this._isView ? c.pt : c.anchor;
    if (!t) return this;
    t[0] = _vx$1(v, 0, t[0]); t[1] = _vx$1(v, 1, t[1]); t[2] = _vx$1(v, 2, t[2]);
    if (!this._isView) {
      if (c.kind === DIAL) c._dialPoint();
      else if (c.kind === AXIS) {
        c.pt[0] = c.anchor[0] + c.s * c.u[0];
        c.pt[1] = c.anchor[1] + c.s * c.u[1];
        c.pt[2] = c.anchor[2] + c.s * c.u[2];
      } else if (c.kind === PLANE) c.seed(c.pt[0], c.pt[1], c.pt[2]);
    }
    return this;
  }

  /** Runtime gate — false suspends grab / solve without disposing. */
  get enabled() { return this._enabled; }
  set enabled(v) {
    this._enabled = !!v;
    if (!this._enabled) {
      if (this._pid !== null && this._pointer) this._pointer.release(this._pid);
      this._grabbed = false; this._hovered = false; this._pid = null;
    }
  }

  /** Release the pointer claim and leave the host. */
  dispose() {
    if (this._pid !== null && this._pointer) this._pointer.release(this._pid);
    this._pid = null;
    this._grabbed = false;
    this._pointer = null;
    this._host.unregister(this);
  }
}

/**
 * Validate the constraint option: a built-in kind, VIEW, or a contract object.
 * @param {*} kind
 * @returns {boolean}
 */
function validConstraint(kind) {
  return kind === SPHERE || kind === PLANE || kind === AXIS || kind === DIAL || kind === VIEW || !!isConstraint(kind);
}

/**
 * @file The pointer router — shared arbitration for overlapping handles.
 * @module host/router
 * @license AGPL-3.0-only
 *
 * Per-handle self-picking double-grabs on overlap: two proxies under one
 * finger each test only themselves and both hit. The router replaces the
 * members' down step with one shared pick: the pointer's ray once, every
 * enabled member's proxy tested, the nearest t wins (a tie keeps the
 * earlier member, as draw order would). Members keep their own move /
 * solve / release machinery, grabbed through their injected _adopt; every
 * unclaimed press of the frame resolves, so several same-frame presses on
 * different members all land. Hover is the same test on unclaimed pointer
 * motion, shared, setting at most one hovered member.
 */


/** Shared pick over a set of handles on one host. */
class PointerRouter {
  /**
   * @param {object}   host     The host context.
   * @param {object[]} handles  Members.
   * @param {{ hover?:boolean }} [opts]  hover defaults to true.
   */
  constructor(host, handles, opts) {
    const o = opts || {};
    this._host = host;
    this._pointer = host.pointer;
    this._handles = [];
    this._hover = o.hover !== false;
    this._hoveredH = null;
    this._hid = null; this._hseq = -1;
    for (const h of handles || []) this.add(h);
  }

  /**
   * Route a handle: its own pointerdown adoption is disabled and the shared
   * pick grabs it through _adopt. Chainable.
   * @param {object} h
   * @returns {PointerRouter} this
   */
  add(h) {
    if (!h || typeof h._proxyT !== 'function') {
      console.error('[host] router.add: not a handle — ignoring.');
      return this;
    }
    if (this._handles.includes(h)) return this;
    h._routed = true;
    h._onRelease = h._onCancel = () => this._unclaim(h);
    this._handles.push(h);
    return this;
  }

  /**
   * Un-route a handle; it self-picks again. Chainable.
   * @param {object} h
   * @returns {PointerRouter} this
   */
  remove(h) {
    const i = this._handles.indexOf(h);
    if (i < 0) return this;
    this._handles.splice(i, 1);
    this._unclaim(h);
    h._routed = false;
    h._onRelease = h._onCancel = null;
    if (this._hoveredH === h) { this._hoveredH = null; h._hovered = false; }
    return this;
  }

  /**
   * Resolve the frame's presses with one shared pick each, refresh hover on
   * pointer motion, then update every member. Call first in the frame, in
   * place of the members' own updates.
   * @returns {boolean} true if any member is grabbed.
   */
  update() {
    const hs = this._handles, src = this._pointer;
    if (!src) return false;
    for (const pr of src.presses) {
      if (src.ownerOf(pr.id) !== null) continue;
      const win = this._sharedPick(pr.x, pr.y);
      if (win && win._pid === null && win.enabled) win._adopt(pr.id, pr.x, pr.y);
    }
    if (this._hover) {
      for (const e of src.pointers.values()) {
        if (e.owner !== null) continue;
        if (e.id === this._hid && e.seq === this._hseq) continue;
        this._hid = e.id; this._hseq = e.seq;
        const win = this._sharedPick(e.x, e.y);
        this._hoveredH = win;
        for (const h of hs) h._hovered = (h === win) || h._grabbed;
        break;
      }
    }
    let g = false;
    for (const h of hs) g = h.update() || g;
    return g;
  }

  // One ray, every enabled member's proxy, the nearest t.
  _sharedPick(x, y) {
    const hs = this._handles;
    const first = hs.find((h) => h.enabled);
    if (!first || !first._ray(x, y)) return null;
    let win = null, best = Infinity;
    for (const h of hs) {
      if (!h.enabled) continue;
      const t = h._proxyT(rayScratch.o, rayScratch.d);
      if (t < best) { best = t; win = h; }
    }
    return win;
  }

  _unclaim(h) {
    const src = this._pointer;
    if (!src) return;
    for (const e of src.pointers.values()) if (e.owner === h) src.release(e.id);
  }

  /** The member under the pointer, or null. */
  hovered() { return this._hoveredH; }

  /** Un-route every member and leave the host. */
  dispose() {
    for (const h of [...this._handles]) this.remove(h);
    this._pointer = null;
    this._host.unregister(this);
  }
}

/**
 * @file Helm factories — a core PoseHelm driven from the host's players.
 * @module host/helm
 * @license AGPL-3.0-only
 *
 * cameraHelm(host, cam, opts) flies a camera state body-relative: the helm is
 * seeded from the state through cameraToPose, integrates each tick in the
 * state's own eye matrix (which equals the pose written last tick, so a
 * forward push flies forward) and writes back through cameraFromPose at the
 * state's gaze distance. poseHelm(host, opts) produces a pose in a declared
 * frame — WORLD, EYE (the host's view bag), SELF (its own pose) or a mat4 —
 * and drives whatever bind() names: a camera state, an { applyPose } sink, a
 * { get, set } accessor or a { pos, rot } object. Both register a player
 * with the host until disposed.
 */


const _pose = { pos: [0, 0, 0], rot: [0, 0, 0, 1] };   // step() output
const _self = { pos: [0, 0, 0], rot: [0, 0, 0, 1] };   // eval() readout for SELF
const _em   = new Float32Array(16);                    // the resolved basis

const _rawMat4 = (m) => (m != null && m.mat4 != null) ? m.mat4 : m;
const _isCameraState = (c) => !!(c && c.eye && c.center && c.up);

function _applyOpts(helm, opts) {
  if (!opts) return;
  if (opts.profile)          helm.profile  = opts.profile;
  if (opts.deadzone != null) helm.deadzone = opts.deadzone;
  if (opts.filter != null)   helm.filter   = opts.filter;
  if (opts.fullScale != null) helm.fullScale = opts.fullScale;
}

/**
 * The basis a pose helm's `from` resolves to: null for WORLD, the view bag's
 * eye matrix for EYE, the helm's own rotation for SELF, a mat4 as given.
 * Exported for a bridge's rig gizmo.
 * @param {PoseHelm} helm
 * @param {object} view  The host's view bag.
 * @param {Float32Array} em  16-element scratch.
 * @returns {ArrayLike<number>|null}
 */
function helmBasis(helm, view, em) {
  const from = helm.from;
  if (from != null && typeof from !== 'string') return _rawMat4(from);
  if (from === EYE)  return view ? view.mat4Eye : null;
  if (from === SELF) return qToMat4(em, helm.eval(_self).rot);
  if (from === WORLD || from == null) return null;
  console.error('[host] poseHelm: `from` must be EYE, WORLD, SELF, or a mat4 frame. Falling back to WORLD.');
  return null;
}

/**
 * Fly a camera state from a live 6-DOF rate stream, body-relative.
 * @param {object} host
 * @param {object} cam   A camera state ({ eye, center, up, … }).
 * @param {{ profile?:object, deadzone?:number, filter?:object, fullScale?:number }} [opts]
 * @returns {PoseHelm} The helm, with dispose().
 */
function cameraHelm(host, cam, opts) {
  const helm = new PoseHelm();
  _applyOpts(helm, opts);
  if (opts && opts.from != null) {
    console.error('[host] cameraHelm: a camera helm is always body-fly and has no `from`. Ignoring it; bind the camera to a poseHelm for view- or world-relative motion.');
  }
  if (_isCameraState(cam)) helm.home(cameraToPose(_pose, cam));
  const player = {
    tick(dt) {
      helm.step(_pose, dt, _isCameraState(cam) ? cameraEye(_em, cam) : null);
      if (_isCameraState(cam)) {
        cameraFromPose(cam, _pose);
        if (helm._onApply) helm._onApply(cam);
      }
      return true;
    },
  };
  helm._onApply = null;   // lib-space seam: a bridge pushes the written state to its camera
  host.players.add(player);
  helm.dispose = () => { host.players.remove(player); host.unregister(helm); return helm; };
  return host.register(helm);
}

/**
 * Integrate a live 6-DOF rate stream into a pose in a declared frame and
 * drive a bound target with it.
 * @param {object} host
 * @param {{ profile?:object, deadzone?:number, filter?:object, fullScale?:number,
 *           from?:string|ArrayLike<number>, bind?:object }} [opts]
 * @returns {PoseHelm} The helm, with bind() and dispose().
 */
function poseHelm(host, opts) {
  const o = opts || {};
  const helm = new PoseHelm();
  _applyOpts(helm, o);
  if (o.from != null) helm.from = o.from;
  let sink = null;

  const player = {
    tick(dt) {
      if (sink) {
        helm.step(_pose, dt, helmBasis(helm, host.view, _em));
        sink(_pose);
      }
      return true;
    },
  };
  host.players.add(player);

  /**
   * Bind the target the helm drives: a camera state (seeded from it, written
   * through cameraFromPose), an { applyPose } sink, a { get, set } accessor
   * (get() seeds, set(pose) writes) or a { pos, rot } object mutated in
   * place. Chainable.
   * @param {object} target
   * @returns {PoseHelm} this
   */
  helm.bind = (target) => {
    if (_isCameraState(target)) {
      helm.home(cameraToPose(_pose, target));
      sink = (pose) => cameraFromPose(target, pose);
    } else if (target && typeof target.get === 'function' && typeof target.set === 'function') {
      helm.home(target.get());
      sink = (pose) => target.set(pose);
    } else if (target && typeof target.applyPose === 'function') {
      sink = (pose) => target.applyPose(pose);
    } else if (target && target.pos && target.rot) {
      helm.home(target);
      sink = (pose) => {
        target.pos[0] = pose.pos[0]; target.pos[1] = pose.pos[1]; target.pos[2] = pose.pos[2];
        target.rot[0] = pose.rot[0]; target.rot[1] = pose.rot[1];
        target.rot[2] = pose.rot[2]; target.rot[3] = pose.rot[3];
      };
    } else {
      console.error('[host] poseHelm: bind() target must be a camera state, an { applyPose } sink, a { get, set } accessor, or a { pos, rot } object. Leaving unbound.');
    }
    return helm;
  };
  helm.dispose = () => { host.players.remove(player); host.unregister(helm); return helm; };
  if (o.bind != null) helm.bind(o.bind);
  return host.register(helm);
}

/**
 * @file Track factories and TrackHandles — core tracks wired to the host's players.
 * @module host/track
 * @license AGPL-3.0-only
 *
 * poseTrack(host, opts) is a core PoseTrack whose activation hooks add and
 * remove its player. cameraTrack(host, cam, opts) is a core CameraTrack whose
 * player evaluates straight into the camera state `cam` — the state is the
 * keyframe shape, so track.eval(cam) is the write — while the track plays, and
 * once more on deactivate so the camera rests on the path. That bound is the
 * point: a stopped track's camera belongs to whoever else drives it — the
 * reader's orbit, the figure's own camera code — so a consumer that renders
 * from the state derives it instead, one evaluation per frame after the tick
 * that advances the transport, which carries a seek, a dragged keyframe and a
 * capture into the state as well and repeats the player's write harmlessly
 * while the track plays. add() with no argument
 * captures `cam`, add({ camera }) any camera-state object. { handles } on
 * either decorates the track with TrackHandles: one VIEW handle per
 * draggable keyframe field (pos / eye / center), an optional rot DIAL per
 * keyframe about a declared axis, all on one router.
 *
 * add(depth) — the transport panel's + button, which passes its depth slider
 * (ui's contract) — authors a keyframe where the camera is looking: on a pose
 * track the pose lands at the centre of the installed camera's frustum at
 * that depth, aimed along the camera's own eye frame; on a camera track the
 * camera state is captured, the depth having nothing to place. A panel whose
 * depth row is hidden passes no depth, which reads the panel's default, 0.5.
 * The frame is the host's view bag — the matrices the last setCamera
 * installed — so the placement is the same on every bridge and needs no
 * renderer here. remove() is the panel's −: no argument retracts the last
 * keyframe, one per call, an index staying the core track's own remove.
 */


// ── Placement — the + button's depth ───────────────────────────────────────
//
// The transport panel hands add() a depth in [0, 1] and expects a keyframe in
// front of the camera. Writer-free placement: unproject the frustum's centre
// through the view bag's P · V inverse and aim the pose along the bag's eye
// frame (E = V⁻¹, its columns right / up / back). The depth is NDC-linear, as
// the p5 transport's was — 0 the near plane's centre, 1 the far plane's — so
// every bridge's own ndcZMin maps it without the caller knowing the API.

const _at  = [0, 0, 0];   // the unprojected frustum centre
const _dir = [0, 0, 0];   // the camera's forward
const _up  = [0, 0, 0];   // the camera's up

/**
 * Append a pose at the centre of the installed camera's frustum, `depth` of
 * the way from the near plane to the far one.
 *
 * @param {object} host
 * @param {function} add    The track's core add, already bound.
 * @param {number} [depth]  NDC-linear depth in [0, 1]; clamped, absent or NaN
 *                          reads the panel's own default, 0.5.
 * @returns {boolean} false when no camera is installed (a stale view bag).
 */
function _addInFront(host, add, depth) {
  const view = host.view;
  if (!view || view.stale) return false;
  const d    = Number.isFinite(depth) ? (depth < 0 ? 0 : depth > 1 ? 1 : depth) : 0.5;
  const ndcZ = view.ndcZMin + d * (1 - view.ndcZMin);
  mapLocation(_at, 0, 0, ndcZ, NDC, WORLD, view, view.vp, view.ndcZMin);
  const e = view.mat4Eye;                        // columns: right · up · back · eye
  _dir[0] = -e[8];  _dir[1] = -e[9];  _dir[2] = -e[10];
  _up[0]  =  e[4];  _up[1]  =  e[5];  _up[2]  =  e[6];
  add({ pos: [_at[0], _at[1], _at[2]], rot: { dir: _dir, up: _up } }, { deduplicate: false });
  return true;
}

// ── Players ────────────────────────────────────────────────────────────────

// remove(): the panel's −, retracting the last authored keyframe — one per
// click, either kind of track. An index stays the core track's own remove, so
// a caller editing by index is unaffected; an empty track removes nothing.
function _wireRemove(track) {
  const coreRemove = track.remove.bind(track);
  track.remove = function (index) {
    return coreRemove(index == null ? track.keyframes.length - 1 : index);
  };
  return track;
}

function _wirePoseTrack(host, track) {
  let player = null;
  track._onActivate = () => {
    player = player || { tick() { track.tick(); return track.playing; } };
    host.players.add(player);
  };
  track._onDeactivate = () => { if (player) host.players.remove(player); };
}

/**
 * A core PoseTrack ticked by the host while it plays.
 * @param {object} host
 * @param {{ handles?:boolean|object }} [opts]
 * @returns {PoseTrack}
 */
function poseTrack(host, opts) {
  const o = opts || {};
  const track = new PoseTrack();
  _wirePoseTrack(host, track);

  // add(depth): the transport's + (see the module header). A spec, or an array
  // of specs, is the core track's own; no argument — the panel with its depth
  // row hidden — places at the panel's default depth, 0.5.
  const coreAdd = track.add.bind(track);
  let warned = false;
  track.add = function (spec, addOpts) {
    if (spec != null && typeof spec !== 'number') return coreAdd(spec, addOpts);
    if (!_addInFront(host, coreAdd, spec) && !warned) {
      warned = true;
      console.warn('[host] poseTrack: add(depth) places a pose against the installed camera, and the view bag is stale — no keyframe added. Call the bridge\'s setCamera first.');
    }
  };

  if (o.handles) track.handles = new TrackHandles(host, track, o.handles, false);
  return _wireRemove(track);
}

/**
 * A core CameraTrack evaluating into the camera state `cam` while it plays, and
 * once when it stops, so the camera rests on the path.
 *
 * The write is bounded by playback on purpose: while a track is stopped the
 * camera it names belongs to whoever else drives it — the reader's orbit, the
 * figure's own camera code — and a factory writing every frame would fight
 * them. A consumer that renders *from* the state therefore derives it: evaluate
 * the track once per frame, after the tick that advances the transport. A seek,
 * a dragged keyframe and a capture reach the state that way too, and the
 * evaluation repeats the player's own write harmlessly while the track plays.
 *
 * @param {object} host
 * @param {object} cam   A camera state ({ eye, center, up, fov, halfHeight, near, far }).
 * @param {{ handles?:boolean|object }} [opts]
 * @returns {CameraTrack}
 */
function cameraTrack(host, cam, opts) {
  const o = opts || {};
  const track = new CameraTrack();
  const state = cam || null;   // the evaluation target; track.camera is informational
  track.camera = state;
  track._onApply = null;       // lib-space seam: a bridge pushes the evaluated state to its camera

  const coreAdd = track.add.bind(track);
  track.add = function (spec, addOpts) {
    // No spec, or the transport panel's depth: a camera keyframe *is* the
    // camera, so there is nothing a placement depth could move — capture.
    if (spec == null || typeof spec === 'number') {
      if (!state) return;
      spec = state;
    } else if (Array.isArray(spec)) {
      for (const s of spec) track.add(s, addOpts);
      return;
    } else if (spec.camera != null) {
      spec = spec.camera;
    }
    coreAdd(spec, addOpts);
  };

  const apply = () => {
    if (!state || track.keyframes.length === 0) return;
    track.eval(state);
    if (track._onApply) track._onApply(state);
  };
  const player = {
    tick() {
      if (!track.playing) return false;
      track.tick();
      apply();
      return track.playing;
    },
  };
  track._onActivate   = () => host.players.add(player);
  track._onDeactivate = () => { host.players.remove(player); apply(); };

  if (o.handles) track.handles = new TrackHandles(host, track, o.handles, true);
  return _wireRemove(track);
}

// ── TrackHandles ───────────────────────────────────────────────────────────

const _vx = (v, i) => (v[i] !== undefined ? v[i] : (i === 0 ? v.x : i === 1 ? v.y : v.z));

/**
 * Per-keyframe manipulators of a track — the factories' `handles` opt,
 * stored at track.handles. Members are internal handles on one router;
 * their hooks are re-exposed with keyframe coordinates: onGrab(index, field,
 * h) / onChange(value, index, field, h) / onRelease / onCancel, field one of
 * 'pos' | 'eye' | 'center' | 'rot'. update() rebuilds the members when the
 * keyframe count changes and re-seeds every idle member from its keyframe.
 * A bridge draws the members itself (`members`).
 */
class TrackHandles {
  /**
   * @param {object} host
   * @param {object} track       PoseTrack | CameraTrack.
   * @param {true|object} opts   `true` for the defaults, or
   *        { center?, rot?, rotRadius?, rotSnap?, grabPx?, snap?, hover? }.
   * @param {boolean} isCamera   CameraTrack (eye / center) vs PoseTrack (pos / rot).
   */
  constructor(host, track, opts, isCamera) {
    const o = opts === true ? {} : (opts || {});
    this._host     = host;
    this._track    = track;
    this._isCamera = !!isCamera;
    this._members    = [];
    this._rotByIndex = new Map();
    this._n          = -1;
    this._enabled    = true;
    /** Last-grabbed keyframe index, null until a grab. @type {number|null} */
    this.selected = null;

    this._grabPx  = Number.isFinite(o.grabPx) ? o.grabPx : 12;
    this._snap    = o.snap    ?? null;
    this._rotSnap = o.rotSnap ?? null;
    this._center  = this._isCamera ? (o.center !== false) : false;
    if (!this._isCamera && o.center !== undefined) console.error('[host] track handles: `center` is CameraTrack-only — ignoring.');
    this._rotAxis   = null;
    this._rotRadius = Number.isFinite(o.rotRadius) ? o.rotRadius : 40;
    if (o.rot != null) {
      if (this._isCamera) {
        console.error('[host] track handles: `rot` is PoseTrack-only — a camera keyframe\'s orientation is its center; drag that instead. Ignoring.');
      } else {
        const ax = _vx(o.rot, 0) ?? 0, ay = _vx(o.rot, 1) ?? 1, az = _vx(o.rot, 2) ?? 0;
        const l = Math.hypot(ax, ay, az) || 1;
        this._rotAxis = [ax / l, ay / l, az / l];
      }
    }

    this.onGrab = null; this.onChange = null; this.onRelease = null; this.onCancel = null;
    this._hover  = o.hover !== false;
    this._router = null;   // made on the first rebuild, once a subclass is constructed
  }

  // The construction seams a bridge subclass overrides so the members are
  // handles it can draw.
  _makeHandle(opts) { return this._host.handle(opts); }
  _makeRouter(opts) { return this._host.router([], opts); }

  /** The members, { h, index, field } each, for a bridge's draw. */
  get members() { return this._members; }
  /** Dot radius in pixels, the members' grabPx. */
  get grabPx() { return this._grabPx; }

  /**
   * Rebuild if the keyframe count changed, re-seed idle members, then route.
   * Call first in the frame, before the orbit.
   * @returns {boolean} true while any member is grabbed.
   */
  update() {
    if (this._track.keyframes.length !== this._n) this._rebuild();
    if (this._enabled) this._syncIdle();
    return this._router.update();
  }

  /** The members' router, made on the first update(). */
  get router() { return this._router; }

  /** Runtime gate — false suspends grab / solve and empties the pick. */
  get enabled() { return this._enabled; }
  set enabled(v) {
    this._enabled = !!v;
    for (const m of this._members) m.h.enabled = this._enabled;
  }

  /** True while any member is grabbed. */
  grabbed() {
    for (const m of this._members) if (m.h.grabbed()) return true;
    return false;
  }

  /** The keyframe index under the pointer (or grabbed), else null. */
  hovered() {
    for (const m of this._members) if (m.h.hovered()) return m.index;
    return null;
  }

  /** Re-seed every idle member from its keyframe. Chainable. */
  sync() { this._syncIdle(); return this; }

  /** Dispose the members and the router, detach from the track. */
  dispose() {
    this._teardown();
    if (this._router) this._router.dispose();
    if (this._track.handles === this) this._track.handles = null;
  }

  _rebuild() {
    if (!this._router) this._router = this._makeRouter({ hover: this._hover });
    this._teardown();
    const n = this._track.keyframes.length;
    this._n = n;
    for (let i = 0; i < n; i++) {
      this._addViewMember(i, this._isCamera ? 'eye' : 'pos');
      if (this._center)  this._addViewMember(i, 'center');
      if (this._rotAxis) this._addRotMember(i);
    }
    if (this.selected != null && this.selected >= n) this.selected = null;
  }

  _teardown() {
    for (const m of this._members) { this._router.remove(m.h); m.h.dispose(); }
    this._members.length = 0;
    this._rotByIndex.clear();
  }

  // A VIEW member bound to kf[field] by index, so set(i, spec) replacing the
  // keyframe never strands it.
  _addViewMember(index, field) {
    const track = this._track;
    const h = this._makeHandle({
      constraint: VIEW, grabPx: this._grabPx, snap: this._snap,
      bind: {
        get: () => track.keyframes[index] ? track.keyframes[index][field] : null,
        set: (v) => {
          const k = track.keyframes[index];
          if (!k) return;
          const a = k[field];
          a[0] = _vx(v, 0); a[1] = _vx(v, 1); a[2] = _vx(v, 2);
        },
      },
    });
    if (!h) return;
    this._wire(h, index, field);
    this._members.push({ h, index, field });
    this._router.add(h);
  }

  // A rot member: one DIAL about the declared axis at the keyframe's
  // position, unbound — θ lands in kf.rot through the hooks.
  _addRotMember(index) {
    const kf = this._track.keyframes[index];
    const h = this._makeHandle({
      constraint: DIAL, anchor: [kf.pos[0], kf.pos[1], kf.pos[2]],
      axis: this._rotAxis, radius: this._rotRadius, grabPx: this._grabPx, snap: this._rotSnap,
    });
    if (!h) return;
    this._wire(h, index, 'rot');
    const m = { h, index, field: 'rot' };
    this._members.push(m);
    this._rotByIndex.set(index, m);
    this._router.add(h);
    this._syncRot(m);
  }

  _wire(h, index, field) {
    const rotWrite = () => {
      const k = this._track.keyframes[index];
      if (k) { const u = this._rotAxis; qFromAxisAngle(k.rot, u[0], u[1], u[2], h.scalar()); }
    };
    h.onGrab = () => {
      this.selected = index;
      if (this.onGrab) this.onGrab(index, field, h);
    };
    h.onChange = (v) => {
      if (field === 'rot') rotWrite();
      else if (field === 'pos') {
        // Forward the dragged position into the keyframe's rot ring now, so
        // the ring never trails the dot by a frame.
        const rm = this._rotByIndex.get(index);
        const k = this._track.keyframes[index];
        if (rm && k) rm.h.anchor(k.pos);
      }
      if (this.onChange) this.onChange(v, index, field, h);
    };
    h.onRelease = () => { if (this.onRelease) this.onRelease(index, field, h); };
    h.onCancel = () => {
      if (field === 'rot') rotWrite();   // a DIAL is unbound: re-derive from the reverted θ
      if (this.onCancel) this.onCancel(index, field, h);
    };
  }

  _syncIdle() {
    for (const m of this._members) {
      if (m.h.grabbed()) continue;
      if (m.field === 'rot') this._syncRot(m);
      else m.h.sync();
    }
  }

  // Anchor the ring at the live keyframe position and set θ to the twist of
  // kf.rot about the axis: θ = 2·atan2(q.xyz · u, q.w).
  _syncRot(m) {
    const k = this._track.keyframes[m.index];
    if (!k) return;
    m.h.anchor(k.pos);
    const u = this._rotAxis;
    const th = 2 * Math.atan2(k.rot[0] * u[0] + k.rot[1] * u[1] + k.rot[2] * u[2], k.rot[3]);
    const c = m.h._constraint;
    if (c.s !== th) { c.s = th; c._dialPoint(); }
  }
}

/**
 * @file Device streams — WebHID and Gamepad rate sources that feed a helm.
 * @module host/stream
 * @license AGPL-3.0-only
 *
 * The measure-by-subscription transports. Each yields lin[3] / ang[3] in raw
 * device units and, once bind(helm) ran, calls helm.feed(lin, ang) every
 * tick of the host's players — the helm's profile does all scaling, so a
 * stream carries no configuration beyond identification and decoding.
 *
 *   hid     — WebHID. connect() must run from a user gesture; reports land
 *             out of band into the stream's buffers and the frame reads the
 *             latest (last-write-wins). Permissions-Policy gates
 *             requestDevice to top-level documents: a stream in an iframe
 *             without allow="hid" reports available === false and never
 *             prompts.
 *   gamepad — the Gamepad API, polled once per tick from
 *             navigator.getGamepads(); an absent axis reads exactly 0.
 *
 * Both expose available and connected for a UI to reflect, and bind(helm),
 * unbind(), dispose().
 */


const _nav = () => (typeof navigator !== 'undefined' ? navigator : null);

/** The 3Dconnexion vendor ids: the SpaceNavigator and its successors. */
const HID_FILTERS = [{ vendorId: 0x046d }, { vendorId: 0x256f }];

/**
 * The SpaceNavigator decode: report 1 carries translation, report 2
 * rotation, each three int16 little-endian lanes; a firmware that packs all
 * six lanes into one report fills both halves from it.
 * @param {DataView} report   The report's data (the id byte excluded).
 * @param {number} reportId
 * @param {number[]} lin  Translation lanes, written.
 * @param {number[]} ang  Rotation lanes, written.
 */
function decodeSpaceNavigator(report, reportId, lin, ang) {
  if (report.byteLength >= 12) {
    for (let i = 0; i < 3; i++) { lin[i] = report.getInt16(2 * i, true); ang[i] = report.getInt16(6 + 2 * i, true); }
    return;
  }
  if (report.byteLength < 6) return;
  const t = reportId === 1 ? lin : reportId === 2 ? ang : null;
  if (!t) return;
  for (let i = 0; i < 3; i++) t[i] = report.getInt16(2 * i, true);
}

/** Whether WebHID's requestDevice is allowed in this document. */
function _hidAllowed() {
  if (typeof document !== 'undefined' && document.featurePolicy && typeof document.featurePolicy.allowsFeature === 'function') {
    return document.featurePolicy.allowsFeature('hid');
  }
  if (typeof window !== 'undefined' && 'top' in window) return window.top === window.self;
  return true;
}

function _stream(host, lin, ang) {
  let helm = null;
  const s = {
    /** The latest translation lanes, raw device units. */
    lin,
    /** The latest rotation lanes, raw device units. */
    ang,
    /** The bound helm, or null. */
    get helm() { return helm; },
    /**
     * Feed a helm every tick. Chainable.
     * @param {object} target  A PoseHelm (anything with feed(lin, ang)).
     * @returns {object} this
     */
    bind(target) {
      if (!target || typeof target.feed !== 'function') {
        console.error('[host] stream: bind() target must have feed(lin, ang). Leaving unbound.');
        return s;
      }
      helm = target;
      return s;
    },
    /** Stop feeding; the helm sees its last rate until it is fed again. */
    unbind() { helm = null; return s; },
  };
  const feed = () => { if (helm) helm.feed(lin, ang); };
  return { s, feed };
}

/**
 * A WebHID rate stream.
 *
 * @param {object} host
 * @param {{ filters?:object[], decode?:function, bind?:object, resume?:boolean,
 *           hid?:object }} [opts]
 *        filters: the requestDevice filters (default HID_FILTERS). decode:
 *        decode(report, reportId, lin, ang) (default decodeSpaceNavigator).
 *        bind: a helm to bind at once. resume (default true): attach a device
 *        this origin was already granted, without a prompt. hid: the WebHID
 *        interface (default navigator.hid) — the seam a test fills.
 * @returns {object} The stream: { available, connected, device, lin, ang,
 *          connect(), bind(helm), unbind(), dispose() }.
 */
function createHid(host, opts) {
  const o = opts || {};
  const hid = o.hid || (_nav() && _nav().hid) || null;
  const filters = o.filters || HID_FILTERS;
  const decode = typeof o.decode === 'function' ? o.decode : decodeSpaceNavigator;
  const lin = [0, 0, 0], ang = [0, 0, 0];
  const { s, feed } = _stream(host, lin, ang);
  let device = null;

  const onReport = (e) => decode(e.data, e.reportId, lin, ang);
  const onDisconnect = (e) => { if (device && e.device === device) detach(); };
  const matches = (d) => filters.length === 0 || filters.some((f) =>
    (f.vendorId == null || f.vendorId === d.vendorId) && (f.productId == null || f.productId === d.productId));

  function detach() {
    if (!device) return;
    device.removeEventListener('inputreport', onReport);
    if (device.opened && typeof device.close === 'function') device.close().catch(() => {});
    device = null;
    s.connected = false;
    lin[0] = lin[1] = lin[2] = ang[0] = ang[1] = ang[2] = 0;
  }

  async function attach(d) {
    detach();
    try { if (!d.opened) await d.open(); }
    catch (e) { console.error('[host] hid: open() failed: ' + e.message + '.'); return false; }
    device = d;
    d.addEventListener('inputreport', onReport);
    s.connected = true;
    return true;
  }

  Object.assign(s, {
    /** Whether WebHID exists and requestDevice is allowed here. */
    available: !!(hid && typeof hid.requestDevice === 'function') && _hidAllowed(),
    /** Whether a device is open and reporting. */
    connected: false,

    /**
     * Prompt for a device (a user gesture is required) and attach the choice.
     * @returns {Promise<boolean>} attached
     */
    async connect() {
      if (!s.available) { console.error('[host] hid: WebHID is unavailable here (a top-level Chromium document over https or localhost).'); return false; }
      let devices;
      try { devices = await hid.requestDevice({ filters }); }
      catch (e) { console.error('[host] hid: requestDevice rejected: ' + e.message + '.'); return false; }
      if (!devices || !devices.length) return false;
      return attach(devices[0]);
    },

    /**
     * Attach a device this origin was already granted, without a prompt.
     * @returns {Promise<boolean>} attached
     */
    async resume() {
      if (!hid || typeof hid.getDevices !== 'function') return false;
      let devices;
      try { devices = await hid.getDevices(); } catch (_) { return false; }
      const d = (devices || []).find(matches);
      return d ? attach(d) : false;
    },

    /** Close the device, remove every listener, unregister from the host. */
    dispose() {
      detach();
      if (hid && typeof hid.removeEventListener === 'function') hid.removeEventListener('disconnect', onDisconnect);
      host.players.remove(player);
      host.unregister(s);
      return s;
    },
  });

  /** The open HIDDevice, or null. */
  Object.defineProperty(s, 'device', { get: () => device, enumerable: true });

  const player = { tick() { feed(); return true; } };
  host.players.add(player);
  if (hid && typeof hid.addEventListener === 'function') hid.addEventListener('disconnect', onDisconnect);
  if (o.bind) s.bind(o.bind);
  if (s.available && o.resume !== false) s.resume();
  return host.register(s);
}

/**
 * The default gamepad map, the standard layout: left stick → lin[0] / lin[1],
 * right stick → ang[1] / ang[0], the triggers → lin[2] (right minus left).
 */
const GAMEPAD_MAP = { lin: [0, 1, { buttons: [6, 7] }], ang: [3, 2, null] };

function _lane(gp, spec) {
  if (spec == null) return 0;
  if (typeof spec === 'number') { const v = gp.axes[spec]; return typeof v === 'number' ? v : 0; }
  if (spec.buttons) {
    const n = gp.buttons[spec.buttons[0]], p = gp.buttons[spec.buttons[1]];
    return (p ? p.value : 0) - (n ? n.value : 0);
  }
  if (spec.button) { const b = gp.buttons[spec.button]; return b ? b.value : 0; }
  return 0;
}

/**
 * A Gamepad rate stream, polled once per tick.
 *
 * @param {object} host
 * @param {{ index?:number, map?:object, bind?:object, gamepads?:function }} [opts]
 *        index: which gamepad (default: the first connected). map: { lin,
 *        ang }, each three lane specs — an axis index, { buttons: [neg,
 *        pos] }, { button } or null (default GAMEPAD_MAP). bind: a helm to
 *        bind at once. gamepads: the poll (default navigator.getGamepads) —
 *        the seam a test fills.
 * @returns {object} The stream: { available, connected, index, lin, ang,
 *          bind(helm), unbind(), dispose() }.
 */
function createGamepad(host, opts) {
  const o = opts || {};
  const nav = _nav();
  const poll = o.gamepads || (nav && typeof nav.getGamepads === 'function' ? () => nav.getGamepads() : null);
  const map = o.map || GAMEPAD_MAP;
  const lin = [0, 0, 0], ang = [0, 0, 0];
  const { s, feed } = _stream(host, lin, ang);

  Object.assign(s, {
    /** Whether the Gamepad API exists. */
    available: !!poll,
    /** Whether the last poll found a gamepad. */
    connected: false,
    /** The gamepad index polled, or null for the first connected. */
    index: o.index ?? null,
    /** Stop polling, unregister from the host. */
    dispose() { host.players.remove(player); host.unregister(s); return s; },
  });

  const player = {
    tick() {
      let gp = null;
      if (poll) {
        const list = poll() || [];
        if (s.index != null) gp = list[s.index] || null;
        else for (let i = 0; i < list.length; i++) if (list[i]) { gp = list[i]; break; }
      }
      s.connected = !!gp;
      for (let i = 0; i < 3; i++) {
        lin[i] = gp ? _lane(gp, map.lin[i]) : 0;
        ang[i] = gp ? _lane(gp, map.ang[i]) : 0;
      }
      feed();
      return true;
    },
  };
  host.players.add(player);
  if (o.bind) s.bind(o.bind);
  return host.register(s);
}

/**
 * @file OBJ text → the arrays shape a bridge's buffer upload takes.
 * @module host/obj
 * @license AGPL-3.0-only
 *
 * Positions, texture coordinates and normals. Faces are fan-triangulated — a
 * quad as 0 1 2 · 2 3 0 — and each distinct position / uv / normal triple
 * becomes one vertex, in order of first use. Indices are 1-based, or negative
 * counting back from the last declared element. Groups, objects, smoothing and
 * materials are ignored.
 */


const _index = (s, count) => {
  const i = parseInt(s, 10);
  return i < 0 ? count + i : i - 1;
};

/**
 * Parse OBJ text into `{ position, normal?, texcoord?, indices }`; normal and
 * texcoord only when some face vertex names one.
 * @param {string} text
 * @returns {{ position:{numComponents:number,data:Float32Array},
 *             normal?:{numComponents:number,data:Float32Array},
 *             texcoord?:{numComponents:number,data:Float32Array},
 *             indices:{numComponents:number,data:Uint32Array} }}
 */
function parseObj(text) {
  const v = [], vt = [], vn = [];
  const position = [], texcoord = [], normal = [], indices = [];
  const seen = new Map();
  let uvs = false, normals = false;

  const vertex = (token) => {
    const [ps, ts, ns] = token.split('/');
    const p = _index(ps, v.length / 3);
    const t = ts ? _index(ts, vt.length / 2) : -1;
    const n = ns ? _index(ns, vn.length / 3) : -1;
    const key = p + '/' + t + '/' + n;
    let id = seen.get(key);
    if (id !== undefined) return id;
    position.push(v[3 * p], v[3 * p + 1], v[3 * p + 2]);
    if (t >= 0) { texcoord.push(vt[2 * t], vt[2 * t + 1]); uvs = true; } else texcoord.push(0, 0);
    if (n >= 0) { normal.push(vn[3 * n], vn[3 * n + 1], vn[3 * n + 2]); normals = true; } else normal.push(0, 0, 0);
    id = position.length / 3 - 1;
    seen.set(key, id);
    return id;
  };

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line || line[0] === '#') continue;
    const parts = line.split(/\s+/);
    switch (parts[0]) {
      case 'v':  v.push(+parts[1], +parts[2], +parts[3]); break;
      case 'vt': vt.push(+parts[1], +(parts[2] ?? 0)); break;
      case 'vn': vn.push(+parts[1], +parts[2], +parts[3]); break;
      case 'f': {
        const ids = parts.slice(1).map(vertex);
        if (ids.length === 4) indices.push(ids[0], ids[1], ids[2], ids[2], ids[3], ids[0]);
        else for (let i = 1; i + 1 < ids.length; i++) indices.push(ids[0], ids[i], ids[i + 1]);
        break;
      }
    }
  }

  const out = {
    position: { numComponents: 3, data: new Float32Array(position) },
    indices: { numComponents: 3, data: new Uint32Array(indices) },
  };
  if (normals) out.normal = { numComponents: 3, data: new Float32Array(normal) };
  if (uvs) out.texcoord = { numComponents: 2, data: new Float32Array(texcoord) };
  return out;
}

/**
 * @file glTF 2.0 → meshes in the arrays shape, the node hierarchy, skins and animation clips.
 * @module host/gltf
 * @license AGPL-3.0-only
 *
 * The format is glTF 2.0 (Khronos Group): a JSON document plus binary buffers,
 * or both in one GLB container. parseGlb splits a container; parseGltf reads
 * the document over its buffers into what a bridge uploads and what tree's
 * skin functions (clipSample, poseWorld, jointPalette) take as they are.
 *
 * Read: triangle primitives (POSITION, NORMAL, TANGENT, TEXCOORD_0, JOINTS_0,
 * WEIGHTS_0, indices), morph targets (POSITION and NORMAL deltas), the
 * material's base colour factor, nodes, skins, animations. Normalised integer
 * accessors arrive as floats; interleaved views are unpacked. Not read:
 * textures, cameras, other primitive modes, sparse accessors and compressed
 * geometry (Draco, meshopt) — the last two throw.
 *
 * Nodes are re-indexed parents first, and every node index in the result
 * (a mesh's, a skin's joints, a channel's) is the new one.
 */


const GLB_MAGIC = 0x46546C67, CHUNK_JSON = 0x4E4F534A, CHUNK_BIN = 0x004E4942;

const COMPONENTS = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT2: 4, MAT3: 9, MAT4: 16 };
const TYPES = {
  5120: Int8Array, 5121: Uint8Array, 5122: Int16Array, 5123: Uint16Array, 5125: Uint32Array, 5126: Float32Array,
};
const NORMALISED = { 5120: 127, 5121: 255, 5122: 32767, 5123: 65535 };
const ATTRIBUTES = {
  POSITION: 'position', NORMAL: 'normal', TANGENT: 'tangent', TEXCOORD_0: 'texcoord',
  JOINTS_0: 'joints', WEIGHTS_0: 'weights',
};
const IDENTITY = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

/**
 * Split a GLB container into its JSON document and binary chunk.
 * @param {ArrayBuffer} buffer
 * @returns {{ json:object, bin:ArrayBuffer|null }}
 */
function parseGlb(buffer) {
  const view = new DataView(buffer);
  if (view.getUint32(0, true) !== GLB_MAGIC) throw new Error('[host] gltf: not a GLB container.');
  if (view.getUint32(4, true) !== 2) throw new Error('[host] gltf: GLB version ' + view.getUint32(4, true) + ', 2 expected.');
  const end = Math.min(view.getUint32(8, true), buffer.byteLength);
  let json = null, bin = null;
  for (let at = 12; at + 8 <= end;) {
    const length = view.getUint32(at, true), type = view.getUint32(at + 4, true);
    const start = at + 8;
    if (type === CHUNK_JSON) json = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, start, length)));
    else if (type === CHUNK_BIN && !bin) bin = buffer.slice(start, start + length);
    at = start + length + (-length & 3);
  }
  if (!json) throw new Error('[host] gltf: GLB without a JSON chunk.');
  return { json, bin };
}

/**
 * Read a glTF document over its buffers.
 *
 * A part is one triangle primitive under one node: `mesh` in the
 * arrays shape (position, normal?, tangent?, texcoord?, joints?, weights?,
 * indices), `targets` its morph targets as delta arrays, `color` the
 * material's base colour factor, `node` the node it hangs from and `skin` its
 * skin's index or −1. A rigid mesh draws under its node's world matrix; a
 * skinned one under its skin's joint palette.
 *
 * @param {object} json  The glTF document.
 * @param {ArrayBuffer[]} buffers  One per `json.buffers` entry.
 * @returns {{ parts:{ name:string, node:number, skin:number, mesh:object,
 *                      targets:{ name:string, position?:object, normal?:object }[], color:number[] }[],
 *             nodes:{ names:string[], parents:Int32Array, rest:Float32Array },
 *             skins:{ name:string, joints:Uint16Array, inverseBind:Float32Array }[],
 *             clips:{ name:string, duration:number, channels:object[] }[] }}
 *          nodes.rest: the rest pose, ten numbers per node (translation, rotation
 *          [x,y,z,w], scale). A clip channel: { node, path, interp, times, values }.
 */
function parseGltf(json, buffers) {
  for (const ext of json.extensionsRequired || []) {
    if (/draco|meshopt/i.test(ext)) throw new Error('[host] gltf: ' + ext + ' is not supported.');
  }
  const cache = new Map();

  const accessor = (index) => {
    if (cache.has(index)) return cache.get(index);
    const a = json.accessors[index];
    if (a.sparse) throw new Error('[host] gltf: sparse accessors are not supported.');
    const n = COMPONENTS[a.type], Type = TYPES[a.componentType], scale = a.normalized && NORMALISED[a.componentType];
    const data = new (scale ? Float32Array : Type)(a.count * n);
    if (a.bufferView !== undefined) {
      const bv = json.bufferViews[a.bufferView];
      const base = (bv.byteOffset || 0) + (a.byteOffset || 0), size = Type.BYTES_PER_ELEMENT;
      const stride = bv.byteStride || n * size;
      const view = new DataView(buffers[bv.buffer]);
      const get = {
        5120: 'getInt8', 5121: 'getUint8', 5122: 'getInt16', 5123: 'getUint16', 5125: 'getUint32', 5126: 'getFloat32',
      }[a.componentType];
      for (let i = 0; i < a.count; i++) {
        for (let c = 0; c < n; c++) {
          const v = view[get](base + i * stride + c * size, true);
          data[i * n + c] = scale ? Math.max(v / scale, -1) : v;
        }
      }
    }
    const out = { numComponents: n, data };
    cache.set(index, out);
    return out;
  };

  // nodes, parents first
  const src = json.nodes || [], parentOf = new Array(src.length).fill(-1);
  src.forEach((node, i) => { for (const c of node.children || []) parentOf[c] = i; });
  const order = [], remap = new Array(src.length).fill(-1);
  const visit = (i) => { remap[i] = order.length; order.push(i); for (const c of src[i].children || []) visit(c); };
  src.forEach((_, i) => { if (parentOf[i] < 0) visit(i); });

  const names = [], parents = new Int32Array(order.length), rest = new Float32Array(order.length * 10);
  const xf = { pos: [0, 0, 0], rot: [0, 0, 0, 1], scl: [1, 1, 1] };
  order.forEach((old, i) => {
    const node = src[old];
    names.push(node.name || '');
    parents[i] = parentOf[old] < 0 ? -1 : remap[parentOf[old]];
    let t = node.translation || [0, 0, 0], r = node.rotation || [0, 0, 0, 1], s = node.scale || [1, 1, 1];
    if (node.matrix) { mat4ToTransform(xf, node.matrix); t = xf.pos; r = xf.rot; s = xf.scl; }
    rest.set(t, i * 10); rest.set(r, i * 10 + 3); rest.set(s, i * 10 + 7);
  });

  // parts: one per triangle primitive per node
  const primitives = (json.meshes || []).map((mesh) => {
    const out = [];
    for (const prim of mesh.primitives) {
      if ((prim.mode ?? 4) !== 4 || prim.attributes.POSITION === undefined) continue;
      const arrays = {};
      for (const [semantic, key] of Object.entries(ATTRIBUTES)) {
        if (prim.attributes[semantic] !== undefined) arrays[key] = accessor(prim.attributes[semantic]);
      }
      const count = arrays.position.data.length / 3;
      const data = prim.indices !== undefined
        ? Uint32Array.from(accessor(prim.indices).data)
        : Uint32Array.from({ length: count }, (_, i) => i);
      arrays.indices = { numComponents: 3, data };
      const targetNames = (mesh.extras && mesh.extras.targetNames) || [];
      const targets = (prim.targets || []).map((target, i) => {
        const t = { name: targetNames[i] || '' };
        if (target.POSITION !== undefined) t.position = accessor(target.POSITION);
        if (target.NORMAL !== undefined) t.normal = accessor(target.NORMAL);
        return t;
      });
      const material = prim.material !== undefined ? json.materials[prim.material] : null;
      const pbr = material && material.pbrMetallicRoughness;
      out.push({ arrays, targets, color: (pbr && pbr.baseColorFactor ? pbr.baseColorFactor : [1, 1, 1, 1]).slice() });
    }
    return { name: mesh.name || '', primitives: out };
  });

  const parts = [];
  order.forEach((old, i) => {
    const node = src[old];
    if (node.mesh === undefined) return;
    const mesh = primitives[node.mesh];
    for (const p of mesh.primitives) {
      parts.push({ name: mesh.name || names[i], node: i, skin: node.skin ?? -1, mesh: p.arrays, targets: p.targets, color: p.color });
    }
  });

  const skins = (json.skins || []).map((skin) => {
    const joints = Uint16Array.from(skin.joints, j => remap[j]);
    const inverseBind = skin.inverseBindMatrices !== undefined
      ? Float32Array.from(accessor(skin.inverseBindMatrices).data)
      : Float32Array.from({ length: joints.length * 16 }, (_, k) => IDENTITY[k % 16]);
    return { name: skin.name || '', joints, inverseBind };
  });

  const clips = (json.animations || []).map((anim) => {
    let duration = 0;
    const channels = [];
    for (const ch of anim.channels) {
      if (ch.target.node === undefined) continue;
      const sampler = anim.samplers[ch.sampler];
      const times = accessor(sampler.input).data, values = accessor(sampler.output).data;
      if (times.length) duration = Math.max(duration, times[times.length - 1]);
      channels.push({ node: remap[ch.target.node], path: ch.target.path, interp: sampler.interpolation || 'LINEAR', times, values });
    }
    return { name: anim.name || '', duration, channels };
  });

  return { parts, nodes: { names, parents, rest }, skins, clips };
}

/**
 * @file Media sources — images, video and Canvas2D rasters for a bridge's textures, models for its buffers.
 * @module host/media
 * @license AGPL-3.0-only
 *
 * A mesh arrives as arrays for a bridge's buffer upload, its bounds beside
 * them: loadMesh. A model keeps a file's structure — parts, hierarchy, skins,
 * clips: loadModel. Both read OBJ (host/obj) and glTF 2.0 (host/gltf), or
 * another format through a parser the caller passes.
 *
 * What a texture upload reads from: an ImageBitmap fetched from a URL, a
 * hidden <video> element from a file or the user's camera, a bitmap drawn
 * through a 2D context (the text path — glyph strips, billboard tags,
 * anything Canvas2D can typeset). No orientation option anywhere: whether
 * rows flip is the bridge's upload, since it depends on the GPU API's
 * texture space.
 */


/**
 * Fetch an image into an ImageBitmap.
 * @param {string} url
 * @param {{ fetch?:object, bitmap?:object }} [opts]
 *        fetch: the fetch init (credentials, mode, …). bitmap: the
 *        createImageBitmap options (premultiplyAlpha, colorSpaceConversion, …).
 * @returns {Promise<ImageBitmap>}
 */
async function loadImage(url, opts) {
  const o = opts || {};
  const res = await fetch(url, o.fetch);
  if (!res.ok) throw new Error('[host] image: ' + url + ' → ' + res.status);
  const blob = await res.blob();
  return o.bitmap ? createImageBitmap(blob, o.bitmap) : createImageBitmap(blob);
}

const _ONE_NODE = () => ({ names: [''], parents: Int32Array.of(-1), rest: Float32Array.of(0, 0, 0, 0, 0, 0, 1, 1, 1, 1) });
const _part = (mesh) => ({ name: '', node: 0, skin: -1, mesh, targets: [], color: [1, 1, 1, 1] });

// A morph target's deltas expanded through its mesh's indices; a target carries no indices of its own.
function _flatTarget(target, indices) {
  const flat = meshFlatten(target, indices);
  delete flat.indices;
  return flat;
}

// The normals option applied to every part, then each mesh's bounds; parts sharing a mesh share the result.
//   undefined / true  the file's normals; smooth ones where a mesh has none
//   'smooth'          always recomputed, summed per position (tree's meshGroups), so a faceted file smooths
//   'flat'            always recomputed on the flattened mesh (tree's meshFlatten), morph targets flattened with it
//   false             nothing computed
function _finish(model, o) {
  const mode = o.normals === undefined || o.normals === true ? 'fill' : o.normals;
  if (mode !== 'fill' && mode !== 'smooth' && mode !== 'flat' && mode !== false) {
    throw new Error("[host] model: normals is one of 'smooth', 'flat', false, or left out.");
  }
  const done = new Map();
  for (const part of model.parts) {
    const source = part.mesh;
    if (done.has(source)) {
      part.mesh = done.get(source);
      if (mode === 'flat') part.targets = part.targets.map(t => _flatTarget(t, source.indices.data));
      continue;
    }
    let mesh = source;
    const bounds = meshBounds({ min: [0, 0, 0], max: [0, 0, 0], center: [0, 0, 0], diag: 0 }, mesh.position.data);
    if (mode === 'flat') {
      mesh = meshFlatten(source);
      const p = mesh.position.data;
      mesh.normal = { numComponents: 3, data: meshNormals(new Float32Array(p.length), p, mesh.indices.data) };
      part.targets = part.targets.map(t => _flatTarget(t, source.indices.data));
    } else if (mode === 'smooth' || (mode === 'fill' && !mesh.normal)) {
      const p = mesh.position.data;
      const groups = meshGroups(new Int32Array(p.length / 3), p, 1e-6 * bounds.diag);
      mesh.normal = { numComponents: 3, data: meshNormals(new Float32Array(p.length), p, mesh.indices.data, { groups }) };
    }
    mesh.bounds = bounds;
    done.set(source, mesh);
    part.mesh = mesh;
  }
  return model;
}

// Fetch and parse into the model shape, before the normals and bounds.
async function _load(url, o, what) {
  const get = async (at, init) => {
    const res = await fetch(at, init);
    if (!res.ok) throw new Error('[host] ' + what + ': ' + at + ' → ' + res.status);
    return res;
  };
  if (typeof o.parse === 'function') {
    const mesh = await o.parse(await (await get(url, o.fetch)).arrayBuffer());
    return { parts: [_part(mesh)], nodes: _ONE_NODE(), skins: [], clips: [] };
  }
  const format = (o.format || (/\.(\w+)(?:[?#].*)?$/.exec(url) || [])[1] || '').toLowerCase();
  if (format !== 'obj' && format !== 'glb' && format !== 'gltf') {
    throw new Error('[host] ' + what + ': ' + url + ' → unknown format; opts.format is one of obj, glb, gltf, or pass opts.parse.');
  }
  const res = await get(url, o.fetch);
  if (format === 'obj') return { parts: [_part(parseObj(await res.text()))], nodes: _ONE_NODE(), skins: [], clips: [] };
  if (format === 'glb') {
    const { json, bin } = parseGlb(await res.arrayBuffer());
    return parseGltf(json, [bin]);
  }
  const json = await res.json();
  const base = typeof location !== 'undefined' ? new URL(url, location.href) : url;
  const resolve = (uri) => {
    if (/^data:/.test(uri)) return uri;
    try { return String(new URL(uri, base)); } catch (e) { return url.replace(/[^/]*$/, '') + uri; }
  };
  const buffers = await Promise.all((json.buffers || []).map(async b => (await get(resolve(b.uri), o.fetch)).arrayBuffer()));
  return parseGltf(json, buffers);
}

/**
 * Fetch a mesh: one object in the arrays shape — position, indices, and
 * normal, tangent, texcoord, color, joints, weights when the file carries them
 * — with `bounds`, its extent `{ min, max, center, diag }`, beside them. A
 * bridge's buffer upload takes it as it is; `size / mesh.bounds.diag` is the
 * scale that fits any file to `size`.
 *
 * Formats: OBJ (host/obj), and glTF 2.0 as `.glb` or `.gltf` (host/gltf) when
 * the file holds one part. A file of several parts rejects, naming loadModel:
 * a mesh is never silently the first of many. `opts.parse` reads any other
 * format: it receives the fetched ArrayBuffer and returns the arrays shape.
 *
 * Normals. Left out, the option keeps the file's normals and gives smooth
 * ones to a mesh that has none. 'smooth' always recomputes them, summed per
 * position, so a file that arrives faceted or cut along a texture seam
 * smooths too. 'flat' always recomputes them on the flattened mesh — every
 * triangle owning its vertices, so the vertex count grows and the indices
 * count up. false computes nothing. The procedure, its limits (no crease
 * angle; grouping by grid cell, at 1e-6 of the bounds' diagonal) and its
 * sources are tree/mesh's. glTF viewers compute flat normals for a file
 * without any; here that is `normals: 'flat'`.
 *
 * @param {string} url
 * @param {{ fetch?:object, format?:string, parse?:function(ArrayBuffer):object,
 *           normals?:'smooth'|'flat'|false }} [opts]  fetch: the fetch init. format: 'obj' |
 *        'glb' | 'gltf' when the URL's extension does not say. parse: a parser for another
 *        format. normals: as above.
 * @returns {Promise<object>} The mesh.
 */
async function loadMesh(url, opts) {
  const o = opts || {};
  const model = await _load(url, o, 'mesh');
  if (model.parts.length !== 1) {
    throw new Error('[host] mesh: ' + url + ' → ' + model.parts.length + ' parts; loadModel keeps them apart.');
  }
  return _finish(model, o).parts[0].mesh;
}

/**
 * Fetch a model with its structure — what a rig, morph targets or animation
 * need; a single mesh is loadMesh's:
 *
 *   { parts: [{ name, node, skin, mesh, targets, color }], nodes, skins, clips }
 *
 * A part is one triangle primitive under one node: `mesh` as loadMesh returns
 * it (the arrays shape with `bounds`, in the part's own space), `targets` its
 * morph targets as delta arrays, `color` its base colour [r, g, b, a], `node`
 * the node it hangs from and `skin` its skin's index or −1. `nodes` is the
 * hierarchy `{ names, parents, rest }`, parents first, `rest` a pose of ten
 * numbers per node; `skins` are `{ name, joints, inverseBind }` and `clips`
 * `{ name, duration, channels }` — what tree's clipSample, poseWorld and
 * jointPalette take. A rigid part draws under its node's world matrix, a
 * skinned one under its skin's joint palette. An OBJ file is one white part
 * under one identity node.
 *
 * The options are loadMesh's; `normals` applies to every part, 'flat'
 * flattening each part's morph targets with its mesh.
 *
 * @param {string} url
 * @param {{ fetch?:object, format?:string, parse?:function(ArrayBuffer):object,
 *           normals?:'smooth'|'flat'|false }} [opts]
 * @returns {Promise<{ parts:object[], nodes:{ names:string[], parents:Int32Array, rest:Float32Array },
 *                     skins:object[], clips:object[] }>}
 */
async function loadModel(url, opts) {
  const o = opts || {};
  return _finish(await _load(url, o, 'model'), o);
}

/**
 * A hidden, muted, inline <video> element from a file or the user's camera.
 * `ready` resolves once metadata arrived (width / height known); a camera
 * denial rejects it. Inside an iframe a camera source needs allow="camera"
 * on the frame.
 *
 * @param {{ src?:string, camera?:boolean|object, loop?:boolean, autoplay?:boolean,
 *           document?:object, media?:object }} opts
 *        src: a media URL. camera: true or getUserMedia video constraints
 *        ({ facingMode, width, height }). loop (default true for src).
 *        autoplay (default true): start() once ready. document / media: the
 *        Document and MediaDevices to use (defaults: the globals) — the seam
 *        a test fills.
 * @returns {{ el:HTMLVideoElement, ready:Promise<object>, width:number, height:number,
 *             start():Promise<void>, stop():object, dispose():object }}
 */
function createVideo(opts) {
  const o = opts || {};
  const doc = o.document || (typeof document !== 'undefined' ? document : null);
  if (!doc) { console.error('[host] video: no document.'); return null; }
  const el = doc.createElement('video');
  el.muted = true;
  el.playsInline = true;
  el.setAttribute('playsinline', '');
  el.setAttribute('muted', '');
  el.style.display = 'none';
  let stream = null;

  const v = {
    /** The <video> element — the bridge's upload source, refreshed each frame. */
    el,
    /** Intrinsic size, known once `ready` resolved. */
    width: 0, height: 0,
    /** Resolves with this source once metadata arrived; rejects on a camera denial or a load error. */
    ready: null,
    /** Play; resolves when playback began. */
    start() { const p = el.play(); return p && typeof p.catch === 'function' ? p.catch((e) => console.error('[host] video: play() failed: ' + e.message + '.')) : Promise.resolve(); },
    /** Pause. Chainable. */
    stop() { el.pause(); return v; },
    /** Stop, release the camera tracks, detach the element. */
    dispose() {
      el.pause();
      if (stream) { for (const t of stream.getTracks()) t.stop(); stream = null; }
      el.removeAttribute('src');
      el.srcObject = null;
      if (typeof el.remove === 'function') el.remove();
      return v;
    },
  };

  v.ready = new Promise((resolve, reject) => {
    const unlisten = () => { el.removeEventListener('loadedmetadata', onMeta); el.removeEventListener('error', onErr); };
    const fail = (msg) => { unlisten(); reject(new Error('[host] video: ' + msg)); };
    const onMeta = () => {
      v.width = el.videoWidth || 0; v.height = el.videoHeight || 0;
      unlisten();
      if (o.autoplay !== false) v.start();
      resolve(v);
    };
    const onErr = () => fail('failed to load ' + (o.src || 'the source') + '.');
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('error', onErr);
    if (o.camera) {
      const media = o.media || (typeof navigator !== 'undefined' && navigator.mediaDevices) || null;
      if (!media || typeof media.getUserMedia !== 'function') { fail('getUserMedia is unavailable here.'); return; }
      media.getUserMedia({ video: o.camera === true ? true : o.camera, audio: false })
        .then((s) => { stream = s; el.srcObject = s; })
        .catch((e) => fail('camera denied: ' + e.message + '.'));
    } else if (o.src) {
      el.loop = o.loop !== false;
      el.src = o.src;
    } else {
      fail('needs { src } or { camera }.');
    }
  });
  v.ready.catch(() => {});   // an unobserved rejection is the caller's to observe, not a crash
  return v;
}

/**
 * Draw through a 2D context and hand the result back as a texture source: an
 * ImageBitmap where OffscreenCanvas exists, else the canvas element itself.
 *
 * @param {function(CanvasRenderingContext2D, number, number):void} draw
 * @param {number} w  Pixel width.
 * @param {number} h  Pixel height.
 * @param {{ document?:object }} [opts]  The Document for the fallback canvas.
 * @returns {ImageBitmap|HTMLCanvasElement|null}
 */
function raster(draw, w, h, opts) {
  const o = opts || {};
  if (typeof OffscreenCanvas !== 'undefined') {
    const c = new OffscreenCanvas(w, h);
    draw(c.getContext('2d'), w, h);
    return c.transferToImageBitmap();
  }
  const doc = o.document || (typeof document !== 'undefined' ? document : null);
  if (!doc) { console.error('[host] raster: no OffscreenCanvas and no document.'); return null; }
  const c = doc.createElement('canvas');
  c.width = w; c.height = h;
  draw(c.getContext('2d'), w, h);
  return c;
}

/**
 * @file The label layer — DOM text over the canvas, anchored in world or screen space.
 * @module host/labels
 * @license AGPL-3.0-only
 *
 * The replacement for WEBGL text() in HUDs, gizmo labels and formula
 * readouts, with the browser's own shaping. One absolutely positioned <div>
 * overlays the canvas as its next sibling, pointer-events: none, sized by
 * the canvas observer; the host sets the canvas parent to position:
 * relative when it is static, and says so in the console once.
 *
 *   labels.set(id, text, x, y, z, opts)     // a world anchor, projected each tick()
 *   labels.setScreen(id, text, sx, sy, opts) // canvas px, the HUD form
 *   labels.remove(id) · labels.clear() · labels.visible
 *   labels.tick()                            // after the view bag is current
 *
 * opts: dx / dy pixel offsets, anchor ('center' default, 'left', 'right'),
 * class — a CSS class beside the layer's own 'host-label', frame — a
 * transient label that lives for the frame it was set in: a gizmo re-sets
 * it every draw and tick() removes it once a frame passes without. A label
 * whose window depth leaves [0, 1] or whose anchor leaves the canvas is
 * hidden, not clamped. Elements are reused by id; tick() writes transforms
 * only, text when the string changed.
 */


const _p = [0, 0, 0];
const _ANCHOR = { center: -50, left: 0, right: -100 };
const _NO_OPTS = {};

/**
 * Create the label layer of a canvas. The layer is inserted at once; it
 * needs a parent, so create it after the canvas is in the document.
 *
 * @param {object} host  Its canvas, view bag and size are read.
 * @param {{ document?:object }} [opts]  The Document (default: the global).
 * @returns {object|null} The layer, or null without a document or a parent.
 */
function createLabels(host, opts) {
  const o = opts || {};
  const doc = o.document || (typeof document !== 'undefined' ? document : null);
  const canvas = host.canvas;
  const parent = canvas && canvas.parentNode;
  if (!doc || !parent) { console.error('[host] labels: the canvas needs a parent element.'); return null; }

  const el = doc.createElement('div');
  el.className = 'host-labels';
  Object.assign(el.style, {
    position: 'absolute', left: '0px', top: '0px', width: '0px', height: '0px',
    pointerEvents: 'none', overflow: 'hidden',
  });
  const cs = typeof getComputedStyle === 'function' ? getComputedStyle(parent) : null;
  if (cs && cs.position === 'static') {
    parent.style.position = 'relative';
    console.info('[host] labels: set the canvas parent to position: relative for the label layer.');
  }
  parent.insertBefore(el, canvas.nextSibling);

  const entries = new Map();   // id → { el, text, x, y, z, dx, dy, ax, screen, cls, sx, sy, shown }
  let width = 0, height = 0, left = -1, top = -1, visible = true;

  const entry = (id, text, o) => {
    let e = entries.get(id);
    if (!e) {
      const span = doc.createElement('span');
      Object.assign(span.style, { position: 'absolute', left: '0px', top: '0px', whiteSpace: 'nowrap', willChange: 'transform' });
      span.style.visibility = 'hidden';
      el.appendChild(span);
      e = { el: span, text: null, x: 0, y: 0, z: 0, dx: 0, dy: 0, ax: -50, screen: false, cls: null, sx: NaN, sy: NaN, shown: false, frame: false, fresh: false };
      entries.set(id, e);
    }
    if (e.text !== text) { e.text = text; e.el.textContent = text; }
    const c = o.class || null;
    if (e.cls !== c) { e.cls = c; e.el.className = c ? 'host-label ' + c : 'host-label'; }
    e.ax = _ANCHOR[o.anchor] ?? -50;
    e.dx = o.dx || 0; e.dy = o.dy || 0;
    e.frame = !!o.frame; e.fresh = true;
    return e;
  };
  const place = (e, sx, sy) => {
    if (sx === e.sx && sy === e.sy) return;
    e.sx = sx; e.sy = sy;
    e.el.style.transform = 'translate(' + sx + 'px, ' + sy + 'px) translate(' + e.ax + '%, -50%)';
  };
  const show = (e, on) => {
    if (e.shown === on) return;
    e.shown = on;
    e.el.style.visibility = on ? '' : 'hidden';
  };

  const labels = {
    /** The layer element. */
    el,
    /** Whether the layer shows; false hides every label at once. */
    get visible() { return visible; },
    set visible(v) { visible = !!v; el.style.display = visible ? '' : 'none'; },
    /** Live label count. */
    get size() { return entries.size; },

    /**
     * Place a label at a world anchor, projected each tick().
     * @param {string} id
     * @param {string} text
     * @param {number} x, y, z  World anchor.
     * @param {{ dx?:number, dy?:number, anchor?:string, class?:string, frame?:boolean }} [opts]
     * @returns {object} this
     */
    set(id, text, x, y, z, opts) {
      const e = entry(id, text, opts || _NO_OPTS);
      e.screen = false; e.x = x; e.y = y; e.z = z;
      return labels;
    },

    /**
     * Place a label in canvas px — the HUD form.
     * @param {string} id
     * @param {string} text
     * @param {number} sx, sy  Canvas px, y down.
     * @param {{ dx?:number, dy?:number, anchor?:string, class?:string, frame?:boolean }} [opts]
     * @returns {object} this
     */
    setScreen(id, text, sx, sy, opts) {
      const e = entry(id, text, opts || _NO_OPTS);
      e.screen = true; e.x = sx; e.y = sy; e.z = 0;
      return labels;
    },

    /** Remove a label. */
    remove(id) {
      const e = entries.get(id);
      if (e) { el.removeChild(e.el); entries.delete(id); }
      return labels;
    },

    /** Remove every label. */
    clear() { for (const id of [...entries.keys()]) labels.remove(id); return labels; },

    /**
     * Project every world label through the view bag and write the
     * transforms; hide what falls outside the canvas or the depth range.
     * Call after the view bag is current for the frame.
     * @returns {object} this
     */
    tick() {
      const ol = canvas.offsetLeft || 0, ot = canvas.offsetTop || 0;
      if (ol !== left || ot !== top) { left = ol; top = ot; el.style.left = ol + 'px'; el.style.top = ot + 'px'; }
      const view = host.view;
      for (const [id, e] of entries) {
        if (e.frame && !e.fresh) { labels.remove(id); continue; }
        e.fresh = false;
        let sx, sy, on;
        if (e.screen) { sx = e.x; sy = e.y; on = true; }
        else if (!view || view.stale) { on = false; }
        else {
          mapLocation(_p, e.x, e.y, e.z, WORLD, SCREEN, view, view.vp, view.ndcZMin);
          sx = _p[0]; sy = _p[1];
          on = _p[2] >= 0 && _p[2] <= 1 && sx >= 0 && sx <= width && sy >= 0 && sy <= height;
        }
        if (on) place(e, sx + e.dx, sy + e.dy);
        show(e, on);
      }
      return labels;
    },

    /**
     * The observer's write: the layer's size in CSS px.
     * @param {number} w
     * @param {number} h
     * @returns {object} this
     */
    resize(w, h) {
      width = w; height = h;
      el.style.width = w + 'px'; el.style.height = h + 'px';
      return labels;
    },

    /** Remove the layer and its labels. */
    dispose() {
      entries.clear();
      if (el.parentNode) el.parentNode.removeChild(el);
      host.unregister(labels);
      return labels;
    },
  };
  labels.resize(host.width || 0, host.height || 0);
  return host.register(labels);
}

/**
 * @file The orbit — the fall-through camera gesture on unclaimed pointers.
 * @module host/orbit
 * @license AGPL-3.0-only
 *
 * What the handle gate yields to: one pointer orbits the camera state about
 * its center, two pointers dolly by their distance and pan by their
 * midpoint, the wheel dollies. update() consumes only unclaimed pointers and
 * returns true when it moved the camera, so
 *
 *   if (!h.update()) orbit.update()
 *
 * reads as the gate. It writes cam only; the next setCamera(cam) installs it.
 * Two-pointer motion is computed from the pointers' own coordinates, never
 * from a browser's pinch synthesis, so it reads the same on every platform;
 * the canvas gets touch-action: none while the orbit lives so the browser
 * does not scroll or zoom the page instead. `damping`, seconds, makes the
 * camera trail the gesture through a first-order lag: each step of a drag —
 * orbit, pan and dolly lanes — and each wheel notch is an impulse the camera
 * drains with that time constant through tree's coast, so it follows the
 * finger about `damping` behind and, after a release, travels the rest of
 * what the finger did and no more, a flick easing out. A fresh touch drops
 * what is still draining, so do home() and enabled = false. The default is
 * 0.2 s; 0 makes the orbit exact.
 *
 * Canvas y runs down; which world direction that is depends on the
 * projection installed in the view bag: a y-up projection (GL's, tree's
 * default) maps a downward drag to the eye's −up, a y-flipped one (p5's,
 * mat4Proj[5] < 0) to +up. The orbit reads that sign off the bag each
 * update, so a drag down always brings the scene down.
 */


const _clamp = { min: 0, max: Infinity };
const _now$1 = () => (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
const EPS = 1e-4;        // a rate below this in every lane ends the drain
const DT_MAX = 0.1;      // seconds, the measured frame's clamp

/**
 * Create an orbit on a camera state.
 *
 * @param {object} host
 * @param {object} cam  The camera state written.
 * @param {{ rotate?:number, pan?:number, zoom?:number, wheel?:number, damping?:number,
 *           minDistance?:number, maxDistance?:number, enabled?:boolean }} [opts]
 *        rotate: radians per pixel (default 0.005). pan / zoom / wheel:
 *        scalars on the two-pointer pan, the pinch and the wheel dolly
 *        (default 1). damping: the lag's time constant in seconds (default
 *        0.2; 0 exact). minDistance / maxDistance: gaze-distance clamps.
 * @returns {object} The orbit: { cam, enabled, rotate, pan, zoom, wheel, damping,
 *          minDistance, maxDistance, update(dt), home(), dispose() }.
 */
function createOrbit(host, cam, opts) {
  const o = opts || {};
  const canvas = host.canvas;
  const home = cameraCopy(createCamera(), cam);
  const tracked = new Map();   // pointer id → { x, y } as last consumed
  let wheelAcc = 0;
  const rate = [0, 0, 0, 0, 0];           // what is still draining: dAz, dEl, panX, panY, log dolly — per second
  const step = [0, 0, 0, 0, 0];           // one frame of it
  let lastNow = 0, wasPressed = false;

  const cancel = () => { for (let i = 0; i < 5; i++) rate[i] = 0; };
  const apply = (s) => {
    if (s[0] !== 0 || s[1] !== 0) cameraOrbit(cam, s[0], s[1]);
    if (s[2] !== 0 || s[3] !== 0) cameraPan(cam, s[2], s[3]);
    if (s[4] !== 0) dolly(Math.exp(s[4]));
  };

  const onWheel = (e) => {
    if (typeof e.preventDefault === 'function') e.preventDefault();
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? (host.height || 1) : 1;
    wheelAcc += (e.deltaY || 0) * unit;
  };
  canvas.addEventListener('wheel', onWheel, { passive: false });
  const touchAction = canvas.style ? canvas.style.touchAction : undefined;
  if (canvas.style) canvas.style.touchAction = 'none';

  const track = (p) => {
    let t = tracked.get(p.id);
    if (!t) { t = { x: p.x, y: p.y }; tracked.set(p.id, t); }
    else { t.x = p.x; t.y = p.y; }
  };
  const distance = () => {
    const e = cam.eye, c = cam.center;
    return Math.hypot(e[0] - c[0], e[1] - c[1], e[2] - c[2]);
  };
  const dolly = (factor) => {
    _clamp.min = orbit.minDistance; _clamp.max = orbit.maxDistance;
    cameraDolly(cam, factor, _clamp);
  };

  const orbit = {
    /** The camera state written. */
    cam,
    /** False makes update() a no-op that drops its pointer memory. */
    enabled: o.enabled !== false,
    /** Radians per pixel of one-pointer drag. */
    rotate: o.rotate ?? 0.005,
    /** Scalar on the two-pointer pan. */
    pan: o.pan ?? 1,
    /** Scalar on the pinch dolly's exponent. */
    zoom: o.zoom ?? 1,
    /** Scalar on the wheel dolly. */
    wheel: o.wheel ?? 1,
    /** The lag's time constant, seconds; 0 makes the orbit exact. */
    damping: o.damping ?? 0.2,
    /** Gaze-distance clamps for every dolly. */
    minDistance: o.minDistance ?? 0,
    maxDistance: o.maxDistance ?? Infinity,

    /**
     * Consume this frame's unclaimed pointer motion and wheel into cam —
     * through the lag when damping is on.
     * @param {number} [dt]  The frame in seconds; measured since the last update when omitted.
     * @returns {boolean} Whether the camera moved.
     */
    update(dt) {
      const now = _now$1();
      if (typeof dt !== 'number') dt = lastNow ? Math.min((now - lastNow) / 1000, DT_MAX) : 0;
      lastNow = now;
      if (!orbit.enabled) { tracked.clear(); wheelAcc = 0; cancel(); wasPressed = false; return false; }
      const src = host.pointer;
      let a = null, b = null;
      for (const p of src.pointers.values()) {
        // a hovering mouse has an entry without a press; only pressed, unclaimed, live pointers drive
        if (!p.down || p.owner !== null || p.up || p.cancel) { tracked.delete(p.id); continue; }
        if (a === null || p.id < a.id) { b = a; a = p; }
        else if (b === null || p.id < b.id) b = p;
      }
      for (const id of tracked.keys()) {
        if ((a === null || id !== a.id) && (b === null || id !== b.id)) tracked.delete(id);
      }
      const tau = orbit.damping > 0 ? orbit.damping : 0;
      const pressed = a !== null;
      if (pressed && !wasPressed) cancel();         // a fresh touch catches the scene: what was still draining is dropped
      wasPressed = pressed;
      let moved = false;
      let az = 0, el = 0, px = 0, py = 0, ld = 0;   // this frame's step
      const view = host.view;
      const ys = view.mat4Proj[5] < 0 ? -1 : 1;   // canvas-down is the eye's −up under a y-up projection, +up under p5's flip
      if (a !== null && b === null) {
        const t = tracked.get(a.id);
        if (t) {
          const dx = a.x - t.x, dy = a.y - t.y;
          if (dx !== 0 || dy !== 0) { az = -dx * orbit.rotate; el = ys * dy * orbit.rotate; }
        }
        track(a);
      } else if (a !== null && b !== null) {
        const ta = tracked.get(a.id), tb = tracked.get(b.id);
        if (ta && tb) {
          const dmx = (a.x + b.x - ta.x - tb.x) / 2, dmy = (a.y + b.y - ta.y - tb.y) / 2;
          if (dmx !== 0 || dmy !== 0) {
            const ratio = pixelRatio(view.mat4Proj, -view.vp[3] || 1, -distance(), view.ndcZMin);
            px = -dmx * ratio * orbit.pan; py = ys * dmy * ratio * orbit.pan;
          }
          const d0 = Math.hypot(ta.x - tb.x, ta.y - tb.y), d1 = Math.hypot(a.x - b.x, a.y - b.y);
          if (d0 > 0 && d1 > 0 && d0 !== d1) ld = orbit.zoom * Math.log(d0 / d1);
        }
        track(a); track(b);
      }
      // the step: exact, or — with damping — an impulse the drain below spreads over time
      if (az !== 0 || el !== 0 || px !== 0 || py !== 0 || ld !== 0) {
        if (tau > 0) { rate[0] += az / tau; rate[1] += el / tau; rate[2] += px / tau; rate[3] += py / tau; rate[4] += ld / tau; }
        else { step[0] = az; step[1] = el; step[2] = px; step[3] = py; step[4] = ld; apply(step); moved = true; }
      }
      if (wheelAcc !== 0) {
        const w = wheelAcc * 0.001 * orbit.wheel;
        wheelAcc = 0;
        if (tau > 0) rate[4] += w / tau;
        else { dolly(Math.exp(w)); moved = true; }
      }
      if (tau > 0 && coastAlive(rate, EPS)) { apply(coastStep(step, rate, dt, tau)); moved = true; }
      return moved;
    },

    /** Restore the pose captured at creation; whatever was draining is dropped. */
    home() { cameraCopy(cam, home); cancel(); return orbit; },

    /** Remove the wheel listener, restore touch-action, unregister from the host. */
    dispose() {
      canvas.removeEventListener('wheel', onWheel);
      if (canvas.style) canvas.style.touchAction = touchAction;
      tracked.clear();
      cancel();
      host.unregister(orbit);
      return orbit;
    },
  };
  return host.register(orbit);
}

/**
 * @file The host context — one per canvas: pointer, view bag, players, loop, observer.
 * @module host
 * @license AGPL-3.0-only
 *
 * The signal host of a program: what it needs from the browser that is
 * neither math nor GPU — a canvas element's pointer input, the frame clock,
 * its size — and the registry its constructs tick from. Pure vanilla DOM;
 * everything it computes goes through @nakednous/tree, nothing it draws.
 *
 *   const host = createHost(canvas, { onFrame })
 *   host.pointer · host.view · host.players
 *   host.width · host.height · host.dpr · host.dt · host.clock()
 *   host.handle(opts) · host.router(handles, opts)
 *   host.cameraHelm(cam, opts) · host.poseHelm(opts)
 *   host.poseTrack(opts) · host.cameraTrack(cam, opts)
 *   host.hid(opts) · host.gamepad(opts)
 *   host.image(url) · host.video(opts) · host.raster(draw, w, h) · host.mesh(url) · host.model(url)
 *   host.labels        // the DOM label layer, created on first access
 *   host.orbit(cam, opts)
 *   host.tick(dt)      // external-loop mode
 *   host.dispose()
 *
 * Two loop modes. With { onFrame } the host runs requestAnimationFrame
 * (overridable through { raf, caf }): each frame computes dt (clamped to
 * 50 ms), ticks the players, calls onFrame(dt, host), then flushes the
 * pointer source. Without it the application, or the p5 adapter from
 * predraw, calls host.tick(dt) = players.tick(dt) and host.pointer.flush()
 * once its consumers ran. Nothing else in the host cares who owns the loop.
 *
 * The frame order inside onFrame: install the camera (which fills
 * host.view), update handles or routers, fall through to the orbit when
 * nothing grabbed, draw.
 */


const _now = () => (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

/**
 * Create the host context of a canvas.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {{ ndcZMin?:number, onFrame?:function(number, object):void, raf?:function,
 *           caf?:function, onSize?:function(number, number, number):void }} [opts]
 *        ndcZMin: the GPU API's NDC-z convention, WEBGL (default) or WEBGPU —
 *        written once, never detected. onFrame: the host's own loop; absent,
 *        external-tick mode. onSize(width, height, dpr): the bridge's chance
 *        to resize its drawing buffer.
 * @returns {object} The host.
 */
function createHost(canvas, opts) {
  const o = opts || {};
  const t0 = _now();
  const constructs = new Set();
  let loop = null, observer = null, labels = null;
  const host = {
    canvas,
    width: 0, height: 0, dpr: 1,
    dt: 0,
    view: null, pointer: null, players: null,

    /** Seconds since the host was created. */
    clock() { return (_now() - t0) / 1000; },

    /**
     * External-loop mode: tick the players with dt (seconds). The caller
     * flushes the pointer source once its consumers ran.
     * @param {number} dt
     */
    tick(dt) { host.dt = dt; host.players.tick(dt); },

    /**
     * A draggable handle on this canvas — see host/handle. Returns null on
     * an invalid `constraint`.
     * @param {object} opts
     * @returns {Handle|null}
     */
    handle(opts) {
      const o = opts || {};
      if (!validConstraint(o.constraint)) {
        console.error('[host] handle: `constraint` must be SPHERE, PLANE, AXIS, DIAL, VIEW, or a contract-conforming constraint object; got ' + String(o.constraint) + '.');
        return null;
      }
      return host.register(new Handle(host, o));
    },

    /**
     * A shared pick over overlapping handles — see host/router.
     * @param {Handle[]} handles
     * @param {{ hover?:boolean }} [opts]
     * @returns {PointerRouter}
     */
    router(handles, opts) { return host.register(new PointerRouter(host, handles, opts)); },

    /** Fly a camera state body-relative from a 6-DOF rate stream — see host/helm. */
    cameraHelm(cam, opts) { return cameraHelm(host, cam, opts); },
    /** Integrate a 6-DOF rate stream into a pose in a declared frame — see host/helm. */
    poseHelm(opts) { return poseHelm(host, opts); },
    /** A core PoseTrack ticked while it plays — see host/track. */
    poseTrack(opts) { return poseTrack(host, opts); },
    /** A core CameraTrack evaluating into a camera state while it plays — see host/track. */
    cameraTrack(cam, opts) { return cameraTrack(host, cam, opts); },
    /** A WebHID 6-DOF rate stream — see host/stream. */
    hid(opts) { return createHid(host, opts); },
    /** A Gamepad rate stream, polled each tick — see host/stream. */
    gamepad(opts) { return createGamepad(host, opts); },
    /** Fetch an image into an ImageBitmap — see host/media. */
    image(url, opts) { return loadImage(url, opts); },
    /** A hidden <video> from a file or the camera, disposed with the host — see host/media. */
    video(opts) { return host.register(createVideo(opts)); },
    /** Draw through a 2D context into a texture source — see host/media. */
    raster(draw, w, h, opts) { return raster(draw, w, h, opts); },
    /** Fetch one mesh — the arrays shape with its bounds — from an OBJ or glTF file, or any format through opts.parse — see host/media. */
    mesh(url, opts) { return loadMesh(url, opts); },
    /** Fetch a model with its structure: parts, nodes, skins, clips — see host/media. */
    model(url, opts) { return loadModel(url, opts); },
    /**
     * The label layer — see host/labels. Created on first access, so a
     * canvas that never labels never gets a layer nor a repositioned parent.
     */
    get labels() {
      if (labels === null) labels = createLabels(host);
      return labels;
    },
    /** Whether the label layer exists, without creating it. */
    get hasLabels() { return labels !== null; },
    /** The fall-through camera gesture on unclaimed pointers — see host/orbit. */
    orbit(cam, opts) { return createOrbit(host, cam, opts); },

    /** Register a construct with dispose() so host.dispose() releases it. */
    register(c) { if (c) constructs.add(c); return c; },
    /** Unregister a construct. */
    unregister(c) { constructs.delete(c); },

    /** Stop the loop, disconnect the observer, remove every listener, dispose every construct. */
    dispose() {
      if (loop) loop.stop();
      for (const c of [...constructs]) { if (typeof c.dispose === 'function') c.dispose(); }
      constructs.clear();
      labels = null;
      observer.dispose();
      host.pointer.dispose();
      host.players.clear();
    },
  };

  host.view = createView({ ndcZMin: o.ndcZMin });
  host.players = createPlayers();
  host.pointer = createPointer(canvas, host.view);
  observer = observeCanvas(canvas, (w, h, dpr) => {
    host.width = w; host.height = h; host.dpr = dpr;
    host.view.resize(w, h);
    if (labels) labels.resize(w, h);
    if (typeof o.onSize === 'function') o.onSize(w, h, dpr);
  });

  if (typeof o.onFrame === 'function') {
    loop = createLoop({
      raf: o.raf, caf: o.caf,
      onFrame: (dt) => {
        host.dt = dt;
        host.players.tick(dt);
        o.onFrame(dt, host);
        host.pointer.flush();
      },
    });
    loop.start();
  }
  return host;
}

export { GAMEPAD_MAP, HID_FILTERS, Handle, PointerRouter, TrackHandles, VIEW, cameraHelm, cameraTrack, createGamepad, createHid, createHost, createLabels, createLoop, createOrbit, createPlayers, createPointer, createVideo, createView, decodeSpaceNavigator, helmBasis, isConstraint, loadImage, loadMesh, loadModel, measureCanvas, observeCanvas, poseHelm, poseTrack, raster, validConstraint };
//# sourceMappingURL=index.js.map
